var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { instanceToPlain, plainToInstance } from "class-transformer";
import { differenceInYears, subDays, subMonths, subWeeks } from "date-fns";
import { EnrollmentFlowType, EventCategory, mapConsentTypeToEventCategory, PatientAnalyticsEvent, } from "healthex-common/analytics.dtos";
import { ConsentStatus, ConsentUIFlow, doesProjectAllowIdentifiedFutureDataUse, getConsentTypesForProject, getDataConsentTypeForProject, getIASConsentTypes, getPrimaryConsentTypeForProject, getPrimaryIASConsentType, isConsentDataSensitivityScopeValidForConsent, primaryConsentTypes, } from "healthex-common/consent.dtos";
import { ConsentDataSensitivityScope } from "healthex-common/consentscopes.dtos";
import { DocumentRetrievalAuthType, DocumentRetrievalFailureReason, DocumentRetrievalLocationStatus, DocumentRetrievalOrigin, DocumentRetrievalTaskSource, DocumentRetrievalTaskStatus, EpicIntegration, } from "healthex-common/document.retrieval.dtos";
import { FeatureFlag } from "healthex-common/features.dtos";
import { GFIDProvider } from "healthex-common/healthcareorganization.dtos";
import { dtcOrganizationId } from "healthex-common/organization.dtos";
import { mockPatientDataVisualizationLiteDto } from "healthex-common/patient.data.visualization.dtos";
import { isPatientActivelySharingConsent, isProjectActiveForSharing } from "healthex-common/patient.sharing.utils";
import { PreviewConsentFlow, PreviewConsentMode, PreviewParticipantType, PreviewRequestType, } from "healthex-common/preview.dtos";
import { isErr } from "healthex-common/result";
import { screenshotResourceLoadingClass, ScreenshotWaitStrategy, } from "healthex-common/screenshot.dtos";
import { isAiOrGeneralDataUse, ProjectType, StudyPeriodicUnit, StudyStatus, StudyTranslationLanguage, StudyVersionStatus, } from "healthex-common/study.dtos";
import { getPosterUrl } from "healthex-common/transcode.dtos";
import { usaStateFromName, USAStates } from "healthex-common/usaStates";
import { UserPermissions, UserType } from "healthex-common/user.dtos";
import { filterNull, getFullName, includes, slugify, unslugify, willNeverHappen, withoutTrailingSlash } from "healthex-common/utils";
import { computed, observable, reaction, when } from "mobx";
import { getFastenPublicId } from "../Infra/fasten.util";
import { i18nSetLanguage } from "../Infra/i18next";
import { getHeapGlobalEventProperties } from "../Infra/LoadHeap";
import { CookieName } from "../Infra/Persistence";
import { NOT_LOADED } from "../Infra/RemoteData";
import { getAnonymousUserId } from "../Logic/AuthManager";
import { SmartAuthErrorReason, SmartAuthProvider } from "../Logic/SmartAuthManager";
import { ClearInitialScreen } from "./ClearAuthManager";
import { FacilitatedFHIRAuthErrorReason, FacilitatedFHIRAuthState } from "./FacilitatedFHIRAuthManager";
import { NamedRoutes } from "./NamedRoutes";
import { patientConsentStepParentId } from "./PatientConsentManagerShared.styles";
import { patientConsentRouteFromStep, patientConsentRouteStep, patientConsentRouteValues, PDDA_ALL_ROUTES, PDDA_SUB_STEP_ROUTES, } from "./PatientConsentRoute";
import { PatientConsentSession, PatientConsentSessionState } from "./PatientConsentSession";
import { MobileBottomNavigationButtonType } from "./PatientResearchDashboardUIManager";
import { effectiveQueryParams } from "./queryParams";
import { findMatchingNamedRouteParent, IAS_PATH, routeEquals } from "./RoutingUIManager";
import { getTranslatedStudyText } from "./TranslationService";
import { getUIHash } from "./uiHash";
export var PatientConsentLink;
(function (PatientConsentLink) {
    PatientConsentLink["LINK"] = "LINK";
})(PatientConsentLink || (PatientConsentLink = {}));
export var PatientConsentExcludedReason;
(function (PatientConsentExcludedReason) {
    PatientConsentExcludedReason["UNDERAGE"] = "UNDERAGE";
    PatientConsentExcludedReason["NOT_IN_UNITED_STATES"] = "NOT_IN_UNITED_STATES";
    PatientConsentExcludedReason["NOT_RECRUITING"] = "NOT_RECRUITING";
    PatientConsentExcludedReason["TRANSLATIONS_NOT_AVAILABLE"] = "TRANSLATIONS_NOT_AVAILABLE";
})(PatientConsentExcludedReason || (PatientConsentExcludedReason = {}));
const CONSENT_FLOW_TERMINAL_ROUTES = ["ENROLLMENT_CONFIRMED", "ENROLLMENT_DECLINED"];
export class PatientConsentUIManager {
    constructor(authManager, routingUIManager, studyManager, iasManager, patientService, stateLawsService, patientResearchDashboardUIManager, consentExpirationService, smartAuthManager, mrnSearchService, analyticsService, browserLanguageUIManager, environment, documentRetrievalManager, persistence, facilitatedFHIRAuthManager, patientPreferencesManager, clearAuthManager, socialLoginAuthManager, featureManager, hostedIdpSsoManager) {
        this.authManager = authManager;
        this.routingUIManager = routingUIManager;
        this.studyManager = studyManager;
        this.iasManager = iasManager;
        this.patientService = patientService;
        this.stateLawsService = stateLawsService;
        this.patientResearchDashboardUIManager = patientResearchDashboardUIManager;
        this.consentExpirationService = consentExpirationService;
        this.smartAuthManager = smartAuthManager;
        this.mrnSearchService = mrnSearchService;
        this.analyticsService = analyticsService;
        this.browserLanguageUIManager = browserLanguageUIManager;
        this.environment = environment;
        this.documentRetrievalManager = documentRetrievalManager;
        this.persistence = persistence;
        this.facilitatedFHIRAuthManager = facilitatedFHIRAuthManager;
        this.patientPreferencesManager = patientPreferencesManager;
        this.clearAuthManager = clearAuthManager;
        this.socialLoginAuthManager = socialLoginAuthManager;
        this.featureManager = featureManager;
        this.hostedIdpSsoManager = hostedIdpSsoManager;
        this.oauthParams = null;
        this.oauthParamsKey = "oauth_params";
        this._session = new PatientConsentSession();
        this.previewMockedLocationData = this.createPreviewMockedLocationData();
        this.isReactingToLogin = false;
        this.pendingPostLoginAction = null;
        this.resumeNavInFlight = false;
        this.redirectingBeforeAPIs = false;
        reaction(() => this.facilitatedFHIRAuthManager.state, () => {
            this.processFacilitatedFHIRAuthState();
        }, { fireImmediately: true, name: "PatientConsentUIManager_facilitatedFHIRAuthManagerState" });
        reaction(() => [this.clearAuthManager.state, this.routingUIManager.currentRoute], () => {
            void this.processClearLoginResultIfNeeded();
        }, { fireImmediately: true, name: "PatientConsentUIManager_clearAuthManagerState" });
        reaction(() => this.smartAuthManager.state, () => {
            this.restoreStateForAuthCallback();
        }, { fireImmediately: true, name: "PatientConsentUIManager_smartAuthManagerState" });
        reaction(() => this.urlParams, () => this.handleUrlParams(), { fireImmediately: true });
        reaction(() => [routingUIManager.currentRoute, this.studyInfo], async () => {
            var _a;
            const studyId = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.studyId;
            if (!studyId)
                return;
            if (this.studyInfo !== NOT_LOADED && this.studyInfo !== null) {
                await this.persistNonOAuthFlowParams();
                if (this.studyInfo.projectType === ProjectType.AI_DATA_USE) {
                    this.handleUrlParams();
                }
            }
        }, { fireImmediately: true });
        reaction(() => [this._session.studyId, this.isInConsentFlow, this.isDataUpdate], async () => {
            if (this._session.patientConsentSessionState !== PatientConsentSessionState.INITIALIZED)
                return;
            this._session.didEnterConsentFlow = false;
            this._session.didTryInitialConsentRedirect = false;
            this.clearRetrievalBatchId();
        }, { fireImmediately: true });
        reaction(() => this.isPatientLoggedIn, async () => {
            var _a;
            this.isReactingToLogin = true;
            try {
                await this.refreshIAL2Info();
                await this.refreshStudyAndPatientInfo();
            }
            finally {
                this.isReactingToLogin = false;
            }
            if (this.isPatientLoggedIn) {
                (_a = this.pendingPostLoginAction) === null || _a === void 0 ? void 0 : _a.call(this);
                this.pendingPostLoginAction = null;
            }
        }, { fireImmediately: true });
        reaction(() => routingUIManager.currentRoute, async () => {
            this.seedEnteredViaResumeFromUrl();
            this.determineGuidedConsentCloseRoute();
            await this.refreshStudyAndPatientInfo();
            await this.patientPreferencesManager.loadPreferences();
        }, { fireImmediately: true });
        reaction(() => this.smartAuthManager.reauthProxyAccess, async (reauthProxyAccess) => {
            if (!this.currentPage)
                return;
            const proxyAccess = reauthProxyAccess === null ? null : { ...reauthProxyAccess };
            if (["ENROLLMENT_PARENT_GUARDIAN_REAUTHORIZATION"].includes(this.currentPage)) {
                this._session.parentGuardianProxyAccess = proxyAccess;
            }
            else if (["ENROLLMENT_ADDITIONAL_PARENT_GUARDIAN_REAUTHORIZATION"].includes(this.currentPage)) {
                const additionalParentGuardianName = (proxyAccess === null || proxyAccess === void 0 ? void 0 : proxyAccess.relatedPerson) ? getFullName(proxyAccess.relatedPerson) : "";
                this._session.isAdditionalParentGuardianNameUnique = additionalParentGuardianName !== this.parentGuardianName;
                this._session.additionalParentGuardianProxyAccess = this._session.isAdditionalParentGuardianNameUnique
                    ? proxyAccess
                    : null;
            }
        }, { fireImmediately: false });
        reaction(() => this.authManager.email, () => {
            if (this.authManager.email) {
                this._session.patientCollectedEmail = this.authManager.email;
                this.persistence.setCookie(CookieName.PATIENT_COLLECTED_EMAIL, this.authManager.email, 365);
            }
        }, { fireImmediately: true });
        const redirectHandler = async (routeParams) => {
            if (!routeParams.step || routeParams.step === "link") {
                return this.patientConsentManagerRouteToRoute(await this.getFirstRoute());
            }
            else {
                return null;
            }
        };
        this.reactiveRedirect(NamedRoutes.PATIENT_CONSENT_ENROLLMENT, redirectHandler);
        this.reactiveRedirect(NamedRoutes.GUIDED_PATIENT_CONSENT_ENROLLMENT, redirectHandler);
        this.reactiveRedirect(NamedRoutes.PATIENT_CONSENT_DATA_UPDATE, redirectHandler);
        this.reactiveRedirect(NamedRoutes.PATIENT_IAS_CONSENT, redirectHandler);
        this.reactiveRedirect(NamedRoutes.PATIENT_IAS_DATA_UPDATE, redirectHandler);
        const resumeRedirectHandler = async (_routeParams) => {
            this._session.enteredViaResume = true;
            const step = patientConsentRouteStep("ENROLLMENT_LOGIN");
            const studyId = effectiveQueryParams().get("studyId");
            if (studyId) {
                return { route: NamedRoutes.PATIENT_CONSENT_ENROLLMENT, routeParams: { studyId, step } };
            }
            return { route: NamedRoutes.PATIENT_IAS_CONSENT, routeParams: { step } };
        };
        this.reactiveRedirect(NamedRoutes.PATIENT_CONSENT_RESUME, resumeRedirectHandler);
        reaction(() => [this._session.enteredViaResume, this.routingUIManager.currentRoute], () => this.syncResumeUrlMarker(), { fireImmediately: true });
    }
    get session() {
        return this._session;
    }
    get studyVersionId() {
        var _a;
        return this.isScreenshotMode ? (_a = this.consentFlowPageData) === null || _a === void 0 ? void 0 : _a.studyVersionId : this._session._studyVersionId;
    }
    get isDataUpdate() {
        var _a, _b, _c;
        if (((_a = this.previewMetadata) === null || _a === void 0 ? void 0 : _a.requestType) === PreviewRequestType.HEALTH_RECORD_UPDATE) {
            return true;
        }
        if (!!((_b = this.routingUIManager.currentRoute) === null || _b === void 0 ? void 0 : _b.routeName.startsWith(NamedRoutes.PATIENT_CONSENT_DATA_UPDATE.routeName)) ||
            !!((_c = this.routingUIManager.currentRoute) === null || _c === void 0 ? void 0 : _c.routeName.startsWith(NamedRoutes.PATIENT_IAS_DATA_UPDATE.routeName))) {
            return true;
        }
        return false;
    }
    get isIAS() {
        var _a, _b;
        if (this.isScreenshotMode && !this.isScreenshotPreviewMode) {
            return !this._session.studyId;
        }
        if (!!((_a = this.routingUIManager.currentRoute) === null || _a === void 0 ? void 0 : _a.routeName.startsWith(NamedRoutes.PATIENT_IAS_CONSENT.routeName)) ||
            !!((_b = this.routingUIManager.currentRoute) === null || _b === void 0 ? void 0 : _b.routeName.startsWith(NamedRoutes.PATIENT_IAS_DATA_UPDATE.routeName))) {
            return true;
        }
        return false;
    }
    updateConsentFlowPageData(data) {
        var _a, _b, _c, _d, _e;
        this._session._consentFlowPageData = data;
        this._session.additionalParentGuardianExemptSignature = (_a = data === null || data === void 0 ? void 0 : data.additionalParentGuardianExemptSignature) !== null && _a !== void 0 ? _a : null;
        this._session.minorParticipantExemptFromAssent = (_b = data === null || data === void 0 ? void 0 : data.minorParticipantExemptFromAssent) !== null && _b !== void 0 ? _b : null;
        this._session.larParticipantExemptSignature = (_c = data === null || data === void 0 ? void 0 : data.larParticipantExemptSignature) !== null && _c !== void 0 ? _c : null;
        this._session.witnessSkipSignature = (_d = data === null || data === void 0 ? void 0 : data.witnessSkipSignature) !== null && _d !== void 0 ? _d : null;
        this._session.hasLoggedInDuringConsent = (_e = data === null || data === void 0 ? void 0 : data.loggedInDuringConsent) !== null && _e !== void 0 ? _e : null;
        if (data === null || data === void 0 ? void 0 : data.isPreview) {
            this._session.documentRetrievalLocations = this.previewMockedLocationData;
            this._session.locationsNeedingAuthentication = this.previewMockedLocationData
                .filter((location) => location.status === DocumentRetrievalLocationStatus.NEEDS_AUTH)
                .map((location) => location.key);
        }
        else {
            this._session.documentRetrievalLocations = [];
            this._session.locationsNeedingAuthentication = [];
        }
    }
    get recordsSummary() {
        var _a, _b;
        return (_b = (_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.patientData) !== null && _b !== void 0 ? _b : null;
    }
    get existingRecordsSummary() {
        var _a, _b;
        return (_b = (_a = this.existingRetrieval) === null || _a === void 0 ? void 0 : _a.patientData) !== null && _b !== void 0 ? _b : null;
    }
    createPreviewMockedDocuments(count, organizationName) {
        return Array.from({ length: count }, (_, index) => ({
            id: `mock-doc-${index + 1}`,
            filename: `document_${index + 1}.pdf`,
            source: organizationName,
            status: DocumentRetrievalTaskStatus.COMPLETE,
        }));
    }
    createPreviewMockedLocation(organizationName, status, documentCount) {
        return {
            key: crypto.randomUUID(),
            name: organizationName,
            logoUrl: null,
            documents: this.createPreviewMockedDocuments(documentCount, organizationName),
            status,
            isExcluded: null,
            authType: null,
            isPrevious: false,
            updatedAt: new Date(),
            source: DocumentRetrievalTaskSource.FFHIR,
        };
    }
    createPreviewMockedLocationData() {
        return [
            this.createPreviewMockedLocation("Riverside General Hospital", DocumentRetrievalLocationStatus.COMPLETE, 3),
            this.createPreviewMockedLocation("Pine Valley Medical Center", DocumentRetrievalLocationStatus.IN_PROGRESS, 1),
            this.createPreviewMockedLocation("Sunset Family Practice", DocumentRetrievalLocationStatus.NEEDS_AUTH, 0),
            this.createPreviewMockedLocation("Metropolitan Orthopedic Associates", DocumentRetrievalLocationStatus.SKIPPED, 0),
        ];
    }
    get isPatientLoggedIn() {
        if (this.isInteractivePreview) {
            return false;
        }
        if (this.isScreenshotPreviewMode) {
            return true;
        }
        if (this.isOnlyClearLoginAllowed) {
            return this.authManager.isLoggedIn && (this.authManager.gfidProvider === GFIDProvider.CLEAR || this.authManager.isProvider);
        }
        return this.authManager.isLoggedIn;
    }
    get isResolvingResumeTarget() {
        return this._session.enteredViaResume && this.isPatientLoggedIn;
    }
    get enteredViaResume() {
        return this._session.enteredViaResume;
    }
    get consentFlowPageData() {
        return this._session._consentFlowPageData;
    }
    get studyType() {
        var _a, _b;
        if (this._session.study === NOT_LOADED)
            return null;
        return (_b = (_a = this._session.study) === null || _a === void 0 ? void 0 : _a.studyType) !== null && _b !== void 0 ? _b : null;
    }
    determineGuidedConsentCloseRoute() {
        if (this.currentPage !== null)
            return;
        if (!findMatchingNamedRouteParent(this.routingUIManager.currentRoute, [
            NamedRoutes.PATIENTS_SEARCH,
            NamedRoutes.PATIENTS_STUDY_DETAILS,
        ])) {
            return;
        }
        if (this.authManager.userType === UserType.HEALTHCARE_ORGANIZATION &&
            this.authManager.permissions.includes(UserPermissions.PROVIDER)) {
            this._session.guidedConsentCloseRoute = this.routingUIManager.currentRoute;
        }
    }
    get isInteractivePreview() {
        if (!this.authManager.isLoggedIn)
            return false;
        switch (this.authManager.userType) {
            case UserType.PATIENT:
            case UserType.SCREENSHOT_USER:
                return false;
            case UserType.HEALTHCARE_ORGANIZATION:
                return !(this._session.guidedConsentPatientMRN && this.authManager.permissions.includes(UserPermissions.PROVIDER));
            default:
                return true;
        }
    }
    get isScreenshotMode() {
        return this.authManager.userType === UserType.SCREENSHOT_USER;
    }
    get isScreenshotPreviewMode() {
        var _a;
        return this.isScreenshotMode && !!((_a = this.consentFlowPageData) === null || _a === void 0 ? void 0 : _a.isPreview);
    }
    get isPopup() {
        return !!this._session.routeBeforePopup;
    }
    get previewMetadata() {
        var _a, _b;
        if (this.isInteractivePreview)
            return this._session.interactivePreviewMetadata;
        return (_b = (_a = this.consentFlowPageData) === null || _a === void 0 ? void 0 : _a.previewMetadata) !== null && _b !== void 0 ? _b : null;
    }
    get isFirstRoute() {
        if (!this.isInConsentFlow || this.pages === NOT_LOADED)
            return false;
        return this.currentPatientConsentRoute === this.pages[0];
    }
    get currentPatientConsentRoute() {
        if (this.isInteractivePreview) {
            return this._session.currentInteractivePreviewRoute;
        }
        else {
            return this.routeToPatientConsentRoute(this.routingUIManager.currentRoute, this.routingUIManager.currentRouteParams);
        }
    }
    get isInConsentFlow() {
        var _a, _b;
        if (this.isInteractivePreview || this.isScreenshotPreviewMode) {
            return this.previewMetadataRepresentsConsentFlow((_b = (_a = this.previewMetadata) === null || _a === void 0 ? void 0 : _a.consentFlow) !== null && _b !== void 0 ? _b : null);
        }
        const currentRoute = this.routingUIManager.currentRoute;
        if (!currentRoute)
            return false;
        return (currentRoute.routeName.startsWith(NamedRoutes.PATIENT_CONSENT_ENROLLMENT.routeName) ||
            currentRoute.routeName.startsWith(NamedRoutes.PATIENT_CONSENT_DATA_UPDATE.routeName) ||
            currentRoute.routeName.startsWith(NamedRoutes.PATIENT_IAS_CONSENT.routeName) ||
            currentRoute.routeName.startsWith(NamedRoutes.PATIENT_IAS_DATA_UPDATE.routeName) ||
            currentRoute.routeName.startsWith(NamedRoutes.GUIDED_PATIENT_CONSENT_ENROLLMENT.routeName));
    }
    get patientName() {
        var _a;
        if (!this.isInteractivePreview && !!((_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.name)) {
            return this.consentingPatientInfo.name;
        }
        else if (this.isInteractivePreview || this.isScreenshotMode) {
            return "Sample Patient";
        }
        else {
            return "Unknown Patient Name";
        }
    }
    get isParentGuardian() {
        return this._session.parentGuardianProxyAccess !== null;
    }
    get parentGuardianName() {
        var _a;
        if (this.isInteractivePreview)
            return "Sample Parent/Guardian";
        const name = ((_a = this._session.parentGuardianProxyAccess) === null || _a === void 0 ? void 0 : _a.relatedPerson)
            ? getFullName(this._session.parentGuardianProxyAccess.relatedPerson)
            : null;
        return name !== null && name !== void 0 ? name : "Unknown Parent/Guardian";
    }
    get additionalParentGuardianName() {
        var _a;
        if (this.isInteractivePreview)
            return "Sample Additional Parent/Guardian";
        const name = ((_a = this._session.additionalParentGuardianProxyAccess) === null || _a === void 0 ? void 0 : _a.relatedPerson)
            ? getFullName(this._session.additionalParentGuardianProxyAccess.relatedPerson)
            : null;
        return name !== null && name !== void 0 ? name : "Unknown Additional Parent/Guardian";
    }
    get patientMRN() {
        var _a, _b, _c;
        if (this.isInteractivePreview)
            return "Sample MRN";
        if (this.isScreenshotMode)
            return (_b = (_a = this.consentFlowPageData) === null || _a === void 0 ? void 0 : _a.patientMRN) !== null && _b !== void 0 ? _b : null;
        if ((_c = this.consentingPatientInfo) === null || _c === void 0 ? void 0 : _c.mrn) {
            return this.consentingPatientInfo.mrn;
        }
        else {
            return null;
        }
    }
    get providerName() {
        var _a;
        if (this.isInteractivePreview)
            return "Sample Provider";
        return (_a = this.authManager.name) !== null && _a !== void 0 ? _a : "Unknown Provider Name";
    }
    get patientFriendlyTitle() {
        if (this.studyInfo === NOT_LOADED || this.studyInfo === null) {
            return "Unknown Study Title";
        }
        return (getTranslatedStudyText(this.studyInfo, "patientFriendlyTitle", this.browserLanguageUIManager.currentLanguage) ||
            "Unknown Study Title");
    }
    get studySectionImages() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return [];
        }
        return this._session.study.studySectionImages;
    }
    get studyInfo() {
        if (this.isIAS)
            return null;
        if (this._session.study !== NOT_LOADED && this._session.study !== null)
            return this._session.study;
        if (this._session.studyPublicInfo !== NOT_LOADED && this._session.studyPublicInfo !== null)
            return this._session.studyPublicInfo;
        return NOT_LOADED;
    }
    get studySponsor() {
        var _a, _b;
        if (this.studyInfo === NOT_LOADED) {
            return "Unknown Study Sponsor";
        }
        return (_b = (_a = this.studyInfo) === null || _a === void 0 ? void 0 : _a.sponsorOrganizationName) !== null && _b !== void 0 ? _b : "Unknown Study Sponsor";
    }
    get studyPrimaryContactName() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return null;
        }
        return this._session.study.sitePrimaryContactName;
    }
    get studyPrimaryContactTitle() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return null;
        }
        return this._session.study.sitePrimaryContactTitle;
    }
    get primaryDiseaseOrConditions() {
        var _a, _b;
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return [];
        }
        const diseases = getTranslatedStudyText(this._session.study, "informedConsentPrimaryDiseaseOrConditions", this.browserLanguageUIManager.currentLanguage);
        if (diseases.length > 0) {
            return diseases.filter((item) => item.length > 0);
        }
        return (_b = (_a = this._session.study.primaryDiseaseOrConditions) === null || _a === void 0 ? void 0 : _a.filter((item) => item.length > 0)) !== null && _b !== void 0 ? _b : [];
    }
    get patientFriendlySummary() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return "";
        }
        return (getTranslatedStudyText(this._session.study, "patientFriendlySummary", this.browserLanguageUIManager.currentLanguage) || "Not specified");
    }
    get irbApprovalStamp() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return "";
        }
        return (getTranslatedStudyText(this._session.study, "informedConsentIRBApprovalStamp", this.browserLanguageUIManager.currentLanguage) || "");
    }
    get primaryContactEmail() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return "";
        }
        return this._session.study.sitePrimaryContactEmail || "Not specified";
    }
    get primaryContactPhone() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return null;
        }
        return this._session.study.sitePrimaryContactPhone;
    }
    get primaryContactLink() {
        var _a;
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return null;
        }
        const link = (_a = this._session.study.sitePrimaryContactLink) === null || _a === void 0 ? void 0 : _a.trim();
        return link !== null && link !== void 0 ? link : null;
    }
    get risksOfEnrollment() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return "";
        }
        return (getTranslatedStudyText(this._session.study, "risksOfEnrollment", this.browserLanguageUIManager.currentLanguage) ||
            "No risks specified.");
    }
    get benefitsOfEnrollment() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return "";
        }
        return (getTranslatedStudyText(this._session.study, "benefitsOfEnrollment", this.browserLanguageUIManager.currentLanguage) || "No benefits specified.");
    }
    get aiToolName() {
        if (this.studyInfo === NOT_LOADED || this.studyInfo === null) {
            return "";
        }
        return (getTranslatedStudyText(this.studyInfo, "aiToolName", this.browserLanguageUIManager.currentLanguage) ||
            "Unknown AI Tool Name");
    }
    get alternativesToParticipation() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return null;
        }
        return (getTranslatedStudyText(this._session.study, "alternativesToParticipation", this.browserLanguageUIManager.currentLanguage) || null);
    }
    get resourceScopes() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return [];
        }
        return this._session.study.consentDataResourceScopes;
    }
    get sensitivityScopes() {
        if (this._session.study === NOT_LOADED || this._session.study === null || !this.isInConsentFlow) {
            return [];
        }
        const consentType = getDataConsentTypeForProject(this._session.study);
        return this._session.study.consentDataSensitivityScopes.filter((scope) => scope !== ConsentDataSensitivityScope.NORMAL && isConsentDataSensitivityScopeValidForConsent(consentType, scope));
    }
    get isFullRecordMode() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return false;
        }
        return this._session.study.fullRecordWithoutRestrictedData === true;
    }
    get exactConsentExpirationDate() {
        var _a;
        if (this.isIAS) {
            return this.consentExpirationService.getConsentExpiration(null, (_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.state);
        }
        if (this._session.study === NOT_LOADED || this._session.study === null)
            return null;
        return this.getExactConsentExpirationFromStudy(this._session.study);
    }
    get consentExpirationDate() {
        if (!this.exactConsentExpirationDate)
            return null;
        return subDays(this.exactConsentExpirationDate, 1);
    }
    get isPDDAGeneralDataUse() {
        if (this.isIAS)
            return true;
        if (this.studyInfo === NOT_LOADED || this.studyInfo === null)
            return false;
        return this.studyInfo.pdda && isAiOrGeneralDataUse(this.studyInfo.projectType);
    }
    get showClassicLoginPage() {
        return !this.isPDDAGeneralDataUse && this.currentPage === "ENROLLMENT_LOGIN";
    }
    get rightToBeContacted() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return false;
        }
        return !!this._session.study.rightToBeContactedByResearcher;
    }
    get futureUseOfDataText() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return "";
        }
        const translatedText = getTranslatedStudyText(this._session.study, "futureUseOfDataText", this.browserLanguageUIManager.currentLanguage);
        return translatedText || "No data sharing text specified.";
    }
    get showBillOfRightsPage() {
        var _a;
        if (this.isInteractivePreview || this.isScreenshotMode)
            return true;
        return ((_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.state) === USAStates.CA;
    }
    get allowsIdentifiedFutureDataUse() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return false;
        }
        return doesProjectAllowIdentifiedFutureDataUse(this._session.study);
    }
    get showSensitiveDataConfirmationPage() {
        if (this.isInteractivePreview || this.isScreenshotMode || !this.authManager.orgState)
            return true;
        return this.stateLawsService.requiresSensistiveDataConfirmation(this.authManager.orgState);
    }
    get isPDDAStudy() {
        if (this.studyInfo === NOT_LOADED || this.studyInfo === null)
            return null;
        return this.studyInfo.pdda;
    }
    get pddaHealthRecordsUpdateFrequency() {
        var _a, _b;
        if (this._session.study === NOT_LOADED || this._session.study === null)
            return null;
        if (!this._session.study.healthRecordsUpdatesEnabled)
            return null;
        const frequency = (_a = this._session.study.healthRecordsUpdatesEvery) !== null && _a !== void 0 ? _a : null;
        const unit = (_b = this._session.study.healthRecordsUpdatesUnit) !== null && _b !== void 0 ? _b : null;
        if (frequency === null || unit === null)
            return null;
        return { frequency, unit };
    }
    get pddaPatientRecordRetrievalRecencyFrequency() {
        var _a, _b;
        if (this._session.study === NOT_LOADED || this._session.study === null)
            return null;
        if (!this._session.study.patientRecordRetrievalRecencyEnabled)
            return null;
        const frequency = (_a = this._session.study.patientRecordRetrievalRecencyEvery) !== null && _a !== void 0 ? _a : null;
        const unit = (_b = this._session.study.patientRecordRetrievalRecencyUnit) !== null && _b !== void 0 ? _b : null;
        if (frequency === null || unit === null)
            return null;
        return { frequency, unit };
    }
    get showTimeForVisitsPage() {
        if (this._session.study === NOT_LOADED || this._session.study === null)
            return null;
        return (this._session.study.timelineVisitsEnabled === true &&
            this._session.study.timelineVisits &&
            this._session.study.timelineVisits.filter((item) => item.period.length > 0 && item.description.length > 0).length > 0);
    }
    get showEnrollmentDataSharingPage() {
        if (this._session.study === NOT_LOADED || this._session.study === null) {
            return false;
        }
        const translatedText = getTranslatedStudyText(this._session.study, "futureUseOfDataText", this.browserLanguageUIManager.currentLanguage);
        return this._session.study.futureUseOfDataEnabled === true && !!translatedText;
    }
    get showParticipationTerminationPage() {
        if (this._session.study === NOT_LOADED || this._session.study === null)
            return null;
        const translatedText = getTranslatedStudyText(this._session.study, "participationTerminationText", this.browserLanguageUIManager.currentLanguage);
        return this._session.study.participationTerminationEnabled === true && !!translatedText;
    }
    get showLeavingStudyEarlyPage() {
        if (this._session.study === NOT_LOADED || this._session.study === null)
            return null;
        const translatedText = getTranslatedStudyText(this._session.study, "leavingStudyEarlyText", this.browserLanguageUIManager.currentLanguage);
        return this._session.study.leavingStudyEarlyEnabled === true && !!translatedText;
    }
    get showCostOfParticipationPage() {
        if (this._session.study === NOT_LOADED || this._session.study === null)
            return null;
        const translatedText = getTranslatedStudyText(this._session.study, "costOfParticipationText", this.browserLanguageUIManager.currentLanguage);
        return this._session.study.costOfParticipationEnabled === true && !!translatedText;
    }
    get showSharingResultsPage() {
        if (this._session.study === NOT_LOADED || this._session.study === null)
            return null;
        const translatedText = getTranslatedStudyText(this._session.study, "sharingResultsText", this.browserLanguageUIManager.currentLanguage);
        return this._session.study.sharingResultsEnabled === true && !!translatedText;
    }
    get showCompensationPage() {
        if (this._session.study === NOT_LOADED || this._session.study === null)
            return null;
        const translatedText = getTranslatedStudyText(this._session.study, "compensationText", this.browserLanguageUIManager.currentLanguage);
        return this._session.study.compensation === true && !!translatedText;
    }
    get excludedReason() {
        if (!this.isIAS && this.studyInfo !== NOT_LOADED && this.studyInfo !== null) {
            if (this.studyInfo.status === StudyStatus.COMPLETED ||
                this.studyInfo.status === StudyStatus.WITHDRAWN ||
                this.studyInfo.isCompletionDateExpired) {
                return PatientConsentExcludedReason.NOT_RECRUITING;
            }
        }
        if (this.isInteractivePreview || this.isScreenshotMode)
            return null;
        if (this.isMinor && !this.isMinorConsentEnabled) {
            return PatientConsentExcludedReason.UNDERAGE;
        }
        if (!this.isIAS && this._session.study !== NOT_LOADED && this._session.study !== null) {
            if (!this.isProvider &&
                this.browserLanguageUIManager.currentLanguage !== StudyTranslationLanguage.en &&
                !this.studyICFTranslationAvailableLanguages.includes(this.browserLanguageUIManager.currentLanguage)) {
                return PatientConsentExcludedReason.TRANSLATIONS_NOT_AVAILABLE;
            }
        }
        return null;
    }
    get videoTranscode() {
        var _a, _b, _c;
        if (this._session.study === NOT_LOADED)
            return null;
        const currentLanguage = this.currentLanguage;
        if (currentLanguage !== "en") {
            let studyLanguage;
            switch (currentLanguage) {
                case "es":
                    studyLanguage = StudyTranslationLanguage.es;
                    break;
                default:
                    break;
            }
            if (studyLanguage) {
                const languageFields = (_b = (_a = this._session.study) === null || _a === void 0 ? void 0 : _a.studyTranslatableFields) === null || _b === void 0 ? void 0 : _b.find((field) => field.language === studyLanguage);
                if (languageFields) {
                    if (languageFields.enrollmentVideo && typeof languageFields.enrollmentVideo === "object") {
                        return languageFields.enrollmentVideo;
                    }
                }
            }
        }
        return (_c = this._session.study) === null || _c === void 0 ? void 0 : _c.enrollmentTranscode;
    }
    get require21CFRPart11ForInformedConsents() {
        var _a;
        return this._session.study !== NOT_LOADED && !!((_a = this._session.study) === null || _a === void 0 ? void 0 : _a.require21CFRPart11ForInformedConsents);
    }
    get needsReauthorization() {
        if (this._session.study === NOT_LOADED || this._session.study === null)
            return false;
        if (this.authManager.gfidProvider === GFIDProvider.CLEAR)
            return false;
        if (this.authManager.userType !== UserType.PATIENT)
            return false;
        if (this.showMinorConsentPages)
            return false;
        return this.require21CFRPart11ForInformedConsents;
    }
    get isProvider() {
        var _a;
        if (((_a = this.previewMetadata) === null || _a === void 0 ? void 0 : _a.consentMode) === PreviewConsentMode.GUIDED)
            return true;
        if (this.isInteractivePreview || this.isScreenshotPreviewMode)
            return false;
        return (this.authManager.userType === UserType.HEALTHCARE_ORGANIZATION &&
            this.authManager.permissions.includes(UserPermissions.PROVIDER));
    }
    get providerId() {
        var _a;
        if (!this.isProvider)
            return null;
        return (_a = this.authManager.providerId) !== null && _a !== void 0 ? _a : null;
    }
    get isProxyAccess() {
        var _a, _b, _c;
        if ((_a = this.consentFlowPageData) === null || _a === void 0 ? void 0 : _a.isProxyAccess)
            return true;
        return !!((_b = this.authManager.proxyAccess) === null || _b === void 0 ? void 0 : _b.isEnabled) && !!((_c = this.authManager.proxyAccess) === null || _c === void 0 ? void 0 : _c.relatedPerson);
    }
    get isParentGuardianAuthRequired() {
        return this.require21CFRPart11ForInformedConsents || !this.isProxyAccess;
    }
    get isMinorConsentEnabled() {
        var _a, _b;
        if (!this.isProvider &&
            this.authManager.gfidProvider !== GFIDProvider.SMART_ON_FHIR &&
            !this.isScreenshotPreviewMode &&
            !this.isInteractivePreview)
            return false;
        return (_b = (this._session.study !== NOT_LOADED && ((_a = this._session.study) === null || _a === void 0 ? void 0 : _a.minorParticipantConsentEnabled))) !== null && _b !== void 0 ? _b : false;
    }
    get isMinor() {
        var _a, _b, _c, _d, _e;
        if (this.isScreenshotMode &&
            (!!((_a = this.consentFlowPageData) === null || _a === void 0 ? void 0 : _a.parentGuardianSignerName) || !!((_b = this.consentFlowPageData) === null || _b === void 0 ? void 0 : _b.minorParticipantSignerName))) {
            return true;
        }
        else if (this.isInteractivePreview) {
            return ((_c = this._session.interactivePreviewMetadata) === null || _c === void 0 ? void 0 : _c.participantType) === PreviewParticipantType.MINOR;
        }
        else {
            return (_e = (_d = this.consentingPatientInfo) === null || _d === void 0 ? void 0 : _d.isUnderage) !== null && _e !== void 0 ? _e : false;
        }
    }
    get showMinorConsentPages() {
        if (this.isScreenshotPreviewMode && this.isMinorConsentEnabled)
            return true;
        return this.isMinor && this.isMinorConsentEnabled;
    }
    get showParticipantConsentPages() {
        if (this.isScreenshotPreviewMode)
            return true;
        return !this.showMinorConsentPages;
    }
    get isAdditionalParentGuardianEnabled() {
        var _a, _b;
        return (_b = (this._session.study !== NOT_LOADED && ((_a = this._session.study) === null || _a === void 0 ? void 0 : _a.additionalParentGuardianEnabled))) !== null && _b !== void 0 ? _b : false;
    }
    checkIfInterimPage(page) {
        return page.endsWith("_INTERIM_PAGE");
    }
    get isInterimPage() {
        if (!this.currentPage)
            return false;
        return this.checkIfInterimPage(this.currentPage);
    }
    get studyICFTranslationAvailableLanguages() {
        var _a, _b;
        if (this._session.study === NOT_LOADED)
            return [];
        const availableLanguages = [StudyTranslationLanguage.en];
        const otherLanguages = (_b = (_a = this._session.study) === null || _a === void 0 ? void 0 : _a.studyTranslatableFields.filter((field) => field.language !== StudyTranslationLanguage.en && field.enabled && field.completed).map((field) => field.language)) !== null && _b !== void 0 ? _b : [];
        availableLanguages.push(...otherLanguages);
        return availableLanguages;
    }
    get currentLanguage() {
        return this.browserLanguageUIManager.currentLanguage;
    }
    get retrieval() {
        var _a;
        if (this.reusingExistingRecords)
            return (_a = this.existingRetrieval) !== null && _a !== void 0 ? _a : null;
        if (!this._session.retrievalBatchId)
            return null;
        const currentRetrieval = this.documentRetrievalManager.retrieval;
        return currentRetrieval && currentRetrieval.batchId === this._session.retrievalBatchId ? currentRetrieval : null;
    }
    get initialLocationsNeedingAuthentication() {
        return this._session.locationsNeedingAuthentication;
    }
    get pendingRetrieval() {
        var _a, _b;
        const patientId = (_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.id;
        if (!patientId)
            return null;
        const currentRetrieval = this.retrieval;
        if (!currentRetrieval)
            return null;
        const matchesCriteria = currentRetrieval.patientId === ((_b = this.consentingPatientInfo) === null || _b === void 0 ? void 0 : _b.id) &&
            !currentRetrieval.finalized &&
            !currentRetrieval.documentSelectionComplete;
        return matchesCriteria ? currentRetrieval : null;
    }
    get locatingPDDERecords() {
        if (this.isInteractivePreview || this.isScreenshotMode)
            return false;
        if (this.isPDDAGeneralDataUse)
            return true;
        if (this.currentPage === null || !PDDA_ALL_ROUTES.includes(this.currentPage))
            return false;
        if (!this.retrieval)
            return false;
        return this.retrieval.tefcaQueries.some((query) => !query.isComplete);
    }
    get canAddLocations() {
        if (!!this.retrieval && this.retrieval.allowOnlyTargetedFacilities === true) {
            return false;
        }
        return true;
    }
    get zeroLocationsFound() {
        if (!!this.retrieval && this.retrieval.locations.length === 0) {
            return true;
        }
        return false;
    }
    get hasPatientConsented() {
        var _a, _b;
        if (this.isInteractivePreview || this.isScreenshotMode || this.isScreenshotPreviewMode) {
            return this.isDataUpdate;
        }
        return (_b = (_a = this._session.consentInfo) === null || _a === void 0 ? void 0 : _a.hasPatientConsented) !== null && _b !== void 0 ? _b : false;
    }
    get excludedPagesForReconsent() {
        let excludedPagesForReconsent = [];
        if (this.isPDDAGeneralDataUse) {
            excludedPagesForReconsent = [
                "ENROLLMENT_WELCOME",
                "ENROLLMENT_EMAIL_COLLECTION",
                "ENROLLMENT_REQUEST_DETAILS_POPUP",
                "ENROLLMENT_HOW_IT_WORKS",
                "ENROLLMENT_LOGIN",
                "ENROLLMENT_DECLINED_ACCESS",
                "ENROLLMENT_SETUP_COMPLETION",
                "ENROLLMENT_VERIFY_OTP",
            ];
        }
        return excludedPagesForReconsent;
    }
    get mayHaveAccessibleRecordsOnceReady() {
        if (!this.retrieval)
            return false;
        if (this.retrieval.finalized) {
            return this.retrieval.documents.some((document) => document.status === DocumentRetrievalTaskStatus.COMPLETE);
        }
        return this._session.documentRetrievalLocations.some((location) => location.status === DocumentRetrievalLocationStatus.COMPLETE ||
            location.status === DocumentRetrievalLocationStatus.IN_PROGRESS);
    }
    setInMemoryLanguage(language) {
        this.browserLanguageUIManager.setInMemoryLanguage(language);
    }
    get isRelevantRoute() {
        return !!findMatchingNamedRouteParent(this.routingUIManager.currentRoute, [
            NamedRoutes.PATIENT_CONSENT_ENROLLMENT,
            NamedRoutes.PATIENT_CONSENT_DATA_UPDATE,
            NamedRoutes.PATIENT_IAS_CONSENT,
            NamedRoutes.PATIENT_IAS_DATA_UPDATE,
            NamedRoutes.GUIDED_PATIENT_CONSENT_ENROLLMENT,
        ]);
    }
    async refreshStudyAndPatientInfo() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
        if (this.redirectingBeforeAPIs && !this.isRelevantRoute) {
            this.redirectingBeforeAPIs = false;
        }
        if (this.redirectToHomeBeforeAPIsIfNecessary())
            return;
        this._session.guidedConsentPatientMRN = (_b = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.mrn) !== null && _b !== void 0 ? _b : null;
        if (this.isInteractivePreview)
            return;
        if (((_c = this.routingUIManager.currentRouteParams) === null || _c === void 0 ? void 0 : _c.studyId) === IAS_PATH) {
            this._session.studyId = undefined;
        }
        else {
            this._session.studyId = (_d = this.routingUIManager.currentRouteParams) === null || _d === void 0 ? void 0 : _d.studyId;
        }
        let isAIFtuComplete = false;
        if (this._session.studyId) {
            this._session.studyPublicInfo = await this.studyManager.getStudyPublicInfo(this._session.studyId, this.studyVersionId ? { studyVersionId: this.studyVersionId } : StudyVersionStatus.ACTIVE);
            if (this._session.studyPublicInfo) {
                this._session._studyVersionId = this._session.studyPublicInfo.studyVersionId;
                if (((_e = this._session.studyPublicInfo) === null || _e === void 0 ? void 0 : _e.projectType) === ProjectType.AI_DATA_USE) {
                    await this.patientPreferencesManager.preferences.waitForValue();
                    const preferences = this.patientPreferencesManager.preferences.value;
                    if (preferences !== NOT_LOADED && preferences) {
                        const orgId = (_g = (_f = this._session.studyPublicInfo) === null || _f === void 0 ? void 0 : _f.addedByOrganization) === null || _g === void 0 ? void 0 : _g.id;
                        const projectId = this._session.studyId;
                        if (orgId && projectId && preferences.aiPartnerFtu) {
                            const ftuKey = `${orgId}:${projectId}`;
                            isAIFtuComplete = preferences.aiPartnerFtu[ftuKey];
                        }
                        console.log(`AI FTU complete: ${isAIFtuComplete}, projectId: ${projectId !== null && projectId !== void 0 ? projectId : "no project id"}`);
                    }
                }
            }
        }
        if (!this.authManager.isLoggedIn) {
            this.setIAL2Info({ ial2Authorized: false, ial2Expired: false });
            await this.redirectIfNecessary();
            return;
        }
        if (((_h = this.routingUIManager.currentRouteParams) === null || _h === void 0 ? void 0 : _h.link) && !this.pathLink && this.currentPage) {
            if (!((_j = this.routingUIManager.currentRouteParams) === null || _j === void 0 ? void 0 : _j.locationIndex)) {
                this.goToPage(this.currentPage);
                return;
            }
        }
        if (!this.isRelevantRoute) {
            this._session.study = NOT_LOADED;
            this._session.studyPublicInfo = NOT_LOADED;
            this._session.guidedConsentPatientSearchResult = NOT_LOADED;
            return;
        }
        if (this._session.studyId) {
            const study = await this.studyManager.getStudy(this._session.studyId, this.studyVersionId ? { studyVersionId: this.studyVersionId } : StudyVersionStatus.ACTIVE);
            this._session.study = study;
            this.authManager.setPatientConsentOrgId((_l = (_k = study === null || study === void 0 ? void 0 : study.addedByOrganization) === null || _k === void 0 ? void 0 : _k.id) !== null && _l !== void 0 ? _l : null);
            if (this.showMinorConsentPages && ((_m = this.authManager.proxyAccess) === null || _m === void 0 ? void 0 : _m.isEnabled) && ((_o = this.authManager.proxyAccess) === null || _o === void 0 ? void 0 : _o.relatedPerson)) {
                this._session.parentGuardianProxyAccess = { ...this.authManager.proxyAccess };
            }
        }
        else {
            this.authManager.setPatientConsentOrgId(dtcOrganizationId);
        }
        await this.enteredConsentFlow();
        await this.refreshIAL2Info();
        if (this._session.guidedConsentPatientMRN && this.authManager.patientConsentOrgId) {
            const patient = await this.mrnSearchService.searchByMRNAndGetPatientDetails(this.authManager.patientConsentOrgId, {
                mrn: this._session.guidedConsentPatientMRN,
            });
            this._session.guidedConsentPatientSearchResult = patient;
        }
        await this.loadPatientConsent();
        if (this.hasPatientConsented && !this._session.hasConsentBeenSubmittedThisFlow && !this.isDataUpdate) {
            if (this.isIAS || (!this.isAIFlow && !!this.isPDDAStudy) || (this.isAIFlow && isAIFtuComplete)) {
                this.routingUIManager.redirectTo(this.isIAS ? NamedRoutes.PATIENT_IAS_DATA_UPDATE : NamedRoutes.PATIENT_CONSENT_DATA_UPDATE, (_p = this.routingUIManager.currentRouteParams) !== null && _p !== void 0 ? _p : undefined);
            }
        }
        else if (!this.hasPatientConsented && this.isDataUpdate) {
            this.routingUIManager.redirectTo(this.isIAS ? NamedRoutes.PATIENT_IAS_CONSENT : NamedRoutes.PATIENT_CONSENT_ENROLLMENT, (_q = this.routingUIManager.currentRouteParams) !== null && _q !== void 0 ? _q : undefined);
        }
        await this.loadExistingRetrieval();
        await this.navigateToResumeTargetIfNeeded();
        if (await this.redirectToWelcomePageOrContinueGuidedConsentIfNecessary())
            return;
        await this.redirectIfNecessary();
    }
    async refreshIAL2Info() {
        var _a;
        if (!((_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.id)) {
            console.warn("No consenting patient info found, skipping IAL2 info setup");
            this.setIAL2Info({ ial2Authorized: false, ial2Expired: false });
            return;
        }
        try {
            const tefcaAuthInfo = await this.authManager.getPatientTEFCAAuthInfo(this.consentingPatientInfo.id);
            this.setIAL2Info(tefcaAuthInfo);
        }
        catch (error) {
            console.error("Get patient tefca auth info failed", error);
            this.setIAL2Info({ ial2Authorized: false, ial2Expired: false });
        }
    }
    setPostLoginAction(action) {
        if (this._session.enteredViaResume)
            return;
        if (this.isPatientLoggedIn && !this.isReactingToLogin) {
            action();
            return;
        }
        this.pendingPostLoginAction = action;
    }
    setIAL2Info(ial2Info) {
        this.persistence.setIAL2Info(ial2Info);
    }
    getIAL2Info() {
        return this.persistence.getIAL2Info();
    }
    get isOnlyClearLoginAllowed() {
        return this.featureManager.isFeatureEnabled(FeatureFlag.ONLY_CLEAR_LOGIN_ALLOWED);
    }
    get isAppleLoginButtonEnabled() {
        return this.featureManager.isFeatureEnabled(FeatureFlag.APPLE_LOGIN_BUTTON_ENABLED);
    }
    async enteredConsentFlow() {
        var _a, _b, _c, _d;
        if (this._session.didEnterConsentFlow)
            return;
        if (this.isInteractivePreview || this.isScreenshotMode)
            return;
        if (!((_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.id))
            return;
        this._session.didEnterConsentFlow = true;
        const studyId = (_b = this._session.studyId) !== null && _b !== void 0 ? _b : null;
        const existing = await this.patientService.getConsentFlowProgress(this.consentingPatientInfo.id, { studyId });
        if (this._session.enteredViaResume) {
            if (existing) {
                this._session._studyVersionId = (_c = existing.studyVersionId) !== null && _c !== void 0 ? _c : undefined;
                this._session.savedConsentedByLink = (_d = existing.consentedByLink) !== null && _d !== void 0 ? _d : false;
            }
            return;
        }
        await this.saveConsentFlowProgress();
        const targetRoute = await this.getFirstRoute();
        const currentRoute = this.currentPatientConsentRoute;
        const pages = this.enrollmentPages;
        const isOnValidPage = !!currentRoute &&
            pages !== NOT_LOADED &&
            !!pages &&
            (pages.includes(currentRoute) || PDDA_SUB_STEP_ROUTES.includes(currentRoute));
        if (!isOnValidPage && currentRoute !== targetRoute) {
            this.goToPage(targetRoute);
        }
    }
    async resolveResumeTarget() {
        var _a;
        if (!this.authManager.isLoggedIn)
            return "ENROLLMENT_LOGIN";
        const firstRoute = await this.getFirstRoute();
        if (this.pages === NOT_LOADED) {
            console.log("Pages are not loaded in resume route resolution");
            return firstRoute;
        }
        const progress = await this.fetchConsentFlowProgressForResume();
        if (!progress)
            return firstRoute;
        const patientId = (_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.id;
        const hasConsented = this.hasPatientConsented;
        let inProgressRetrieval = null;
        if (patientId && hasConsented && (progress === null || progress === void 0 ? void 0 : progress.retrievalStartedAt)) {
            inProgressRetrieval = await this.restoreExistingInProgressRetrievalIfAny();
        }
        const firstPageAfterConsent = this.pages.includes("ENROLLMENT_USE_EXISTING_RECORDS")
            ? "ENROLLMENT_USE_EXISTING_RECORDS"
            : "ENROLLMENT_PDDA_RECORDS_LOADING";
        const target = (() => {
            if (inProgressRetrieval)
                return "ENROLLMENT_PDDA_RECORDS_LOADING";
            if (this.pages.includes("ENROLLMENT_CLEAR_UPGRADE"))
                return "ENROLLMENT_CLEAR_UPGRADE";
            if (!hasConsented)
                return "ENROLLMENT_PARTICIPATION_SIGNATURE";
            return firstPageAfterConsent;
        })();
        if (!this.pages.includes(target)) {
            console.error("Resume target not in available pages", { target, pages: this.pages });
            return firstRoute;
        }
        return target;
    }
    async fetchConsentFlowProgressForResume() {
        var _a, _b;
        const patientId = (_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.id;
        if (!patientId)
            return null;
        try {
            return await this.patientService.getConsentFlowProgress(patientId, {
                studyId: (_b = this._session.studyId) !== null && _b !== void 0 ? _b : null,
            });
        }
        catch (e) {
            console.warn("Failed to load consent flow progress for resume", e);
            return null;
        }
    }
    seedEnteredViaResumeFromUrl() {
        if (this._session.enteredViaResume)
            return;
        if (effectiveQueryParams().get("resume") !== "true")
            return;
        this._session.enteredViaResume = true;
    }
    syncResumeUrlMarker() {
        if (this.isInteractivePreview || this.isScreenshotMode)
            return;
        if (!this.isInConsentFlow)
            return;
        const urlHasMarker = effectiveQueryParams().get("resume") === "true";
        if (this._session.enteredViaResume === urlHasMarker)
            return;
        const rawHash = window.location.hash.startsWith("#") ? window.location.hash.substring(1) : window.location.hash;
        const [pathPart, queryPart] = rawHash.split("?");
        const params = new URLSearchParams(queryPart !== null && queryPart !== void 0 ? queryPart : "");
        if (this._session.enteredViaResume) {
            params.set("resume", "true");
        }
        else {
            params.delete("resume");
        }
        const queryStr = params.toString();
        const newHash = queryStr ? `${pathPart}?${queryStr}` : pathPart;
        const baseUrl = window.location.href.split("#")[0];
        window.history.replaceState(null, "", `${baseUrl}#${newHash}`);
    }
    async navigateToResumeTargetIfNeeded() {
        var _a, _b;
        if (!this._session.enteredViaResume)
            return;
        if (this.resumeNavInFlight)
            return;
        if (this.isInteractivePreview || this.isScreenshotMode)
            return;
        if (!((_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.id))
            return;
        const pages = this.enrollmentPages;
        if (pages === NOT_LOADED || !pages || pages.length === 0)
            return;
        this.resumeNavInFlight = true;
        try {
            const target = await this.resolveResumeTarget();
            try {
                await this.patientService.markConsentFlowProgressResumed(this.consentingPatientInfo.id, {
                    studyId: (_b = this._session.studyId) !== null && _b !== void 0 ? _b : null,
                });
            }
            catch (e) {
                console.warn("Failed to mark consent flow progress as resumed", e);
            }
            if (this.currentPatientConsentRoute !== target) {
                this.goToPage(target, { replace: true });
            }
        }
        finally {
            this._session.enteredViaResume = false;
            this.resumeNavInFlight = false;
        }
    }
    async loadPatientConsent() {
        var _a, _b, _c;
        if (!((_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.id)) {
            return;
        }
        try {
            if (this._session.studyId) {
                if (this._session.study === NOT_LOADED || this._session.study === null || !this.authManager.patientConsentOrgId) {
                    return;
                }
                const consentTypes = getConsentTypesForProject(this._session.study, this.allowsIdentifiedFutureDataUse);
                for (const consentType of consentTypes) {
                    const evaluatedConsent = await this.patientService.hasPatientConsentedToStudy(this.consentingPatientInfo.id, this._session.studyId, consentType, true);
                    if (consentType ===
                        getPrimaryConsentTypeForProject({ projectType: (_b = this._session.study) === null || _b === void 0 ? void 0 : _b.projectType, pdda: (_c = this._session.study) === null || _c === void 0 ? void 0 : _c.pdda })) {
                        this._session.consentInfo = evaluatedConsent;
                    }
                }
            }
            else if (this.authManager.patientConsentOrgId) {
                try {
                    const consentTypes = getIASConsentTypes();
                    for (const consentType of consentTypes) {
                        const evaluatedConsent = await this.patientService.hasPatientConsentedToIAS(this.consentingPatientInfo.id, consentType, true);
                        if (consentType === getPrimaryIASConsentType()) {
                            this._session.consentInfo = evaluatedConsent;
                        }
                    }
                }
                catch (e) {
                    console.error("Check patient IAS consent API failed", e);
                }
            }
        }
        catch (e) {
            console.error("Check patient consented API failed", e);
        }
    }
    async loadExistingRetrieval() {
        var _a;
        try {
            if (!((_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.id)) {
                return;
            }
            const retrieval = await this.documentRetrievalManager.getMostRecentFinalizedTaskBatch(this.consentingPatientInfo.id, true);
            this._session.existingRetrieval = retrieval;
        }
        catch (e) {
            console.error("Load existing retrieval failed", e);
        }
    }
    async getFirstRoute() {
        while (this.pages === NOT_LOADED) {
            await when(() => this.pages !== NOT_LOADED);
        }
        return this.pages[0];
    }
    async startInteractivePreview(studyId, studyVersionId, previewMetadata) {
        this.reset();
        this._session.studyId = studyId;
        this._session._studyVersionId = studyVersionId;
        this._session.studyPublicInfo = await this.studyManager.getStudyPublicInfo(studyId, { studyVersionId });
        this._session.study = await this.studyManager.getStudy(this._session.studyId, { studyVersionId });
        this._session.interactivePreviewMetadata = previewMetadata;
        this._session.documentRetrievalLocations = this.previewMockedLocationData;
        this._session.locationsNeedingAuthentication = this.previewMockedLocationData
            .filter((location) => location.status === DocumentRetrievalLocationStatus.NEEDS_AUTH)
            .map((location) => location.key);
        this._session.contactMethod = null;
        this._session.contactEmail = null;
        this._session.contactPhone = null;
        this.setIAL2Info({ ial2Authorized: true, ial2Expired: false });
        if (!this.isInConsentFlow)
            throw new Error("Expected to be in a patient consent flow");
        await i18nSetLanguage(previewMetadata.language);
        this._session.currentInteractivePreviewRoute = await this.getFirstRoute();
    }
    redirectToExcludedPageIfNecessary() {
        if (!this.currentPatientConsentRoute)
            return false;
        if (this.excludedReason !== null) {
            if (!["ENROLLMENT_EXCLUDED"].includes(this.currentPatientConsentRoute)) {
                this.goToPage("ENROLLMENT_EXCLUDED");
            }
            return true;
        }
        return false;
    }
    async redirectToWelcomePageOrContinueGuidedConsentIfNecessary() {
        if (!this.currentPatientConsentRoute)
            return false;
        if (this._session.didTryInitialConsentRedirect)
            return false;
        this._session.didTryInitialConsentRedirect = true;
        return this.isProvider && this.excludedReason === null;
    }
    redirectToHomeBeforeAPIsIfNecessary() {
        if (this.redirectingBeforeAPIs)
            return true;
        if (this.pages === NOT_LOADED)
            return false;
        if (this.isScreenshotMode)
            return false;
        if (!this.isRelevantRoute)
            return false;
        const currentConsentRoute = this.currentPatientConsentRoute;
        const previousConsentRoute = this.routeToPatientConsentRoute(this.routingUIManager.previousRoute, this.routingUIManager.previousRouteParams);
        if (!this.isInteractivePreview &&
            !this.isScreenshotPreviewMode &&
            (previousConsentRoute === "ENROLLMENT_CONFIRMED" || previousConsentRoute === "ENROLLMENT_DECLINED") &&
            currentConsentRoute !== "ENROLLMENT_CONFIRMED" &&
            currentConsentRoute !== "ENROLLMENT_DECLINED") {
            this.redirectingBeforeAPIs = true;
            this.routingUIManager.redirectTo(this.homePageAfterConsent);
            return true;
        }
        return false;
    }
    async redirectIfNecessary() {
        if (this.pages === NOT_LOADED)
            return;
        if (this.isScreenshotMode)
            return;
        const currentRoute = this.routingUIManager.currentRoute;
        if (!findMatchingNamedRouteParent(currentRoute, [
            NamedRoutes.PATIENT_CONSENT_ENROLLMENT,
            NamedRoutes.PATIENT_CONSENT_DATA_UPDATE,
            NamedRoutes.PATIENT_IAS_CONSENT,
            NamedRoutes.PATIENT_IAS_DATA_UPDATE,
            NamedRoutes.GUIDED_PATIENT_CONSENT_ENROLLMENT,
        ])) {
            return;
        }
        if (this.redirectToExcludedPageIfNecessary()) {
            return;
        }
        const currentRouteName = this.currentPatientConsentRoute;
        const routePrefix = "ENROLLMENT";
        if (currentRouteName === `${routePrefix}_EXCLUDED` && this.excludedReason === null) {
            this.goToPage(`${routePrefix}_WELCOME`);
        }
        else if (!this.isScreenshotPreviewMode && this.isInterimPage) {
            this.goToPage(`${routePrefix}_WELCOME`);
        }
        else if (this.currentPage) {
            if (!this.isPopup &&
                this.enrollmentPages &&
                this.enrollmentPages !== NOT_LOADED &&
                !this.enrollmentPages.includes(this.currentPage) &&
                !PDDA_SUB_STEP_ROUTES.includes(this.currentPage)) {
                this.goToPage("ENROLLMENT_WELCOME");
            }
        }
    }
    get currentPage() {
        var _a;
        if (this.isInteractivePreview)
            return this._session.currentInteractivePreviewRoute;
        if (!this.isInConsentFlow)
            return null;
        const rawStep = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.step;
        if (!rawStep)
            return null;
        return patientConsentRouteFromStep(unslugify(rawStep));
    }
    get pathLink() {
        var _a, _b, _c;
        if (this.isInteractivePreview)
            return null;
        if (!this.isInConsentFlow)
            return null;
        const rawLink = (_b = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.link) !== null && _b !== void 0 ? _b : (_c = this.routingUIManager.currentRouteParams) === null || _c === void 0 ? void 0 : _c.step;
        if (!rawLink)
            return null;
        const link = unslugify(rawLink);
        if (!Object.values(PatientConsentLink).includes(link))
            return null;
        if (!link)
            return null;
        return link;
    }
    get consentedByLink() {
        return this.pathLink === PatientConsentLink.LINK || this._session.savedConsentedByLink;
    }
    get currentPageIndex() {
        if (this.currentPage === null || this.pages === NOT_LOADED)
            return null;
        const index = this.pages.indexOf(this.currentPage);
        return index === -1 ? null : index;
    }
    get patientCollectedEmail() {
        var _a, _b, _c, _d;
        return ((_d = (_b = (_a = this.persistence.getCookieValue(CookieName.PATIENT_COLLECTED_EMAIL)) !== null && _a !== void 0 ? _a : this.session.patientCollectedEmail) !== null && _b !== void 0 ? _b : (_c = this.socialLoginAuthManager.pendingSocialLoginData) === null || _c === void 0 ? void 0 : _c.email) !== null && _d !== void 0 ? _d : null);
    }
    get mockedExistingRetrieval() {
        return {
            batchId: "123",
            previousBatchId: null,
            patientId: "123",
            createdAt: new Date(),
            isDataUpdate: false,
            tefcaQueries: [],
            locations: [],
            documents: [],
            documentDeliveries: [],
            documentSelectionComplete: true,
            finalized: true,
            patientHasDownloaded: false,
            patientDeliveryExpiration: null,
            origin: DocumentRetrievalOrigin.PATIENT,
            initiatingStudyId: "123",
            patientData: { ...mockPatientDataVisualizationLiteDto, name: "Sample Patient" },
        };
    }
    get existingRetrieval() {
        if (this.isInteractivePreview || this.isScreenshotPreviewMode) {
            return this.mockedExistingRetrieval;
        }
        return this._session.existingRetrieval;
    }
    get existingRetrievalLastUpdatedAt() {
        var _a, _b, _c;
        const retrieval = this.existingRetrieval;
        const summary = this.existingRecordsSummary;
        const dates = filterNull([(_a = retrieval === null || retrieval === void 0 ? void 0 : retrieval.createdAt) !== null && _a !== void 0 ? _a : null, (_b = retrieval === null || retrieval === void 0 ? void 0 : retrieval.lastBackgroundRefreshAt) !== null && _b !== void 0 ? _b : null, (_c = summary === null || summary === void 0 ? void 0 : summary.createdAt) !== null && _c !== void 0 ? _c : null]);
        if (dates.length === 0) {
            return null;
        }
        return [...dates].sort((a, b) => b.getTime() - a.getTime())[0];
    }
    get retrievalRecencyPolicyCutoffDate() {
        var _a;
        const study = this.session.study;
        if (study === NOT_LOADED || study === null)
            return null;
        const every = Number(study === null || study === void 0 ? void 0 : study.patientRecordRetrievalRecencyEvery);
        const unit = (_a = study === null || study === void 0 ? void 0 : study.patientRecordRetrievalRecencyUnit) !== null && _a !== void 0 ? _a : null;
        if (!study.patientRecordRetrievalRecencyEnabled || unit === null || Number.isNaN(every) || every <= 0) {
            return null;
        }
        const now = new Date();
        switch (unit) {
            case StudyPeriodicUnit.DAY: {
                return subDays(now, every);
            }
            case StudyPeriodicUnit.WEEK: {
                return subWeeks(now, every);
            }
            case StudyPeriodicUnit.MONTH: {
                return subMonths(now, every);
            }
            default:
                willNeverHappen(unit);
        }
        return null;
    }
    get showUseExistingRecordsPage() {
        var _a;
        const userIsConsenting = !this.isDataUpdate;
        const userCameFromLink = this.pathLink === PatientConsentLink.LINK;
        const userIsConnectingOauth = this.isOAuthFlow;
        if (this.existingRetrieval === null)
            return false;
        if (this.isIAS) {
            return userIsConsenting || userCameFromLink;
        }
        if (this.session.study === NOT_LOADED) {
            console.error("Study not loaded");
            return false;
        }
        const study = this.session.study;
        const projectType = (_a = study === null || study === void 0 ? void 0 : study.projectType) !== null && _a !== void 0 ? _a : null;
        if (projectType === ProjectType.AI_DATA_USE) {
            return userIsConsenting || userIsConnectingOauth;
        }
        if (projectType === ProjectType.GENERAL_DATA_USE) {
            if (this.isInteractivePreview || this.isScreenshotPreviewMode)
                return true;
            if (this.retrievalRecencyPolicyCutoffDate === null)
                return true;
            return (this.existingRetrievalLastUpdatedAt !== null && this.existingRetrievalLastUpdatedAt > this.retrievalRecencyPolicyCutoffDate);
        }
        return false;
    }
    get updatingRecords() {
        return this._session.userChoseToUpdateRecords !== false;
    }
    get gettingLatestRecords() {
        return this._session.userChoseToUpdateRecords === true;
    }
    get reusingExistingRecords() {
        return this._session.userChoseToUpdateRecords === false;
    }
    get showEmailCollectionPage() {
        if (this.isInteractivePreview)
            return false;
        if (!this.isPatientLoggedIn) {
            return this._session.selectedLoginType === "CLEAR" && !this.patientCollectedEmail;
        }
        else {
            return this.currentPage === "ENROLLMENT_EMAIL_COLLECTION" && this.authManager.gfidProvider === GFIDProvider.CLEAR;
        }
    }
    get hasCompleteDemographicsFromClear() {
        var _a;
        if (!((_a = this.getIAL2Info()) === null || _a === void 0 ? void 0 : _a.ial2Authorized))
            return false;
        const info = this.authManager.consentingPatientInfo;
        if (!info)
            return false;
        return !!info.firstName && !!info.lastName && !!info.dateOfBirth && !!info.phone;
    }
    get showAccountSetupPages() {
        if (this.currentPage === "ENROLLMENT_SETUP_COMPLETION" || this.currentPage === "ENROLLMENT_VERIFY_OTP") {
            return true;
        }
        if (this.hasCompleteDemographicsFromClear)
            return false;
        if (this.socialLoginAuthManager.shouldShowAccountSetupScreen === true) {
            return true;
        }
        if (!this.isPatientLoggedIn) {
            return false;
        }
        const clearAuthData = this.clearAuthManager.getClearAuthData();
        return (this.currentPage === "ENROLLMENT_CLEAR_UPGRADE" &&
            (clearAuthData === null || clearAuthData === void 0 ? void 0 : clearAuthData.initialScreen) === ClearInitialScreen.PATIENT_CONSENT_CLEAR_UPGRADE);
    }
    get enrollmentPages() {
        var _a, _b, _c;
        if (!this.isIAS && this.studyInfo === NOT_LOADED)
            return NOT_LOADED;
        if (!this.isIAS && this.studyInfo === null)
            return null;
        if (this.getIAL2Info() === null)
            return null;
        if (!this.isInConsentFlow)
            return null;
        const pages = [];
        pages.push("ENROLLMENT_WELCOME");
        if (!this.isInteractivePreview) {
            pages.push("ENROLLMENT_REQUEST_DETAILS_POPUP");
        }
        pages.push("ENROLLMENT_HOW_IT_WORKS");
        pages.push("ENROLLMENT_LOGIN");
        if (this.showEmailCollectionPage) {
            pages.push("ENROLLMENT_EMAIL_COLLECTION");
        }
        if ((!((_a = this.getIAL2Info()) === null || _a === void 0 ? void 0 : _a.ial2Authorized) && !this.authManager.blockedFromUsingIAL2) || this.authManager.accountLinkingConflict) {
            pages.push("ENROLLMENT_CLEAR_UPGRADE");
        }
        if (this.showAccountSetupPages) {
            pages.push("ENROLLMENT_SETUP_COMPLETION");
            if (!this.authManager.phoneVerified || this.currentPage === "ENROLLMENT_VERIFY_OTP") {
                pages.push("ENROLLMENT_VERIFY_OTP");
            }
        }
        if (!this.isPatientLoggedIn && !this.isInteractivePreview && !this.isScreenshotMode && !this.isScreenshotPreviewMode) {
            return pages;
        }
        if (!this.hasPatientConsented && ((_c = (_b = this._session.consentInfo) === null || _b === void 0 ? void 0 : _b.consentRecord) === null || _c === void 0 ? void 0 : _c.consentStatus) === ConsentStatus.OPTED_OUT) {
            pages.push("ENROLLMENT_DECLINED_ACCESS");
        }
        if (!this.isDataUpdate) {
            pages.push("ENROLLMENT_PARTICIPATION_SIGNATURE");
        }
        if (this.isScreenshotPreviewMode && !this.isDataUpdate) {
            pages.push("ENROLLMENT_INFORMED_CONSENT_PREVIEW_OPTED_IN_PARTICIPANT_CONSENT_INTERIM_PAGE");
        }
        if (this.showUseExistingRecordsPage) {
            pages.push("ENROLLMENT_USE_EXISTING_RECORDS");
        }
        if (this.isDataUpdate || !!this._session.patientInformedConsentEnabled || this.isScreenshotPreviewMode) {
            if (this.updatingRecords) {
                if (this.isScreenshotMode) {
                    if (this._session.documentRetrievalLocations.length > 0) {
                        pages.push("ENROLLMENT_PDDA_RECORDS_LOADING_SCREENSHOT_RESULTS_FOUND_LIST");
                        pages.push("ENROLLMENT_PDDA_RECORDS_LOADING_SCREENSHOT_DOCUMENT_SELECTION");
                        pages.push("ENROLLMENT_PDDA_RECORDS_LOADING_SCREENSHOT_REVIEW_AND_SHARE");
                    }
                }
                else {
                    pages.push(...PDDA_ALL_ROUTES);
                }
            }
            if (!(this.isScreenshotMode && this.isIAS)) {
                pages.push("ENROLLMENT_CONFIRMED");
            }
        }
        if (!this.isDataUpdate && (!this._session.patientInformedConsentEnabled || this.isScreenshotPreviewMode)) {
            if (this.isScreenshotPreviewMode) {
                pages.push("ENROLLMENT_INFORMED_CONSENT_PREVIEW_OPTED_OUT_INTERIM_PAGE");
            }
            if (this.isScreenshotPreviewMode) {
                pages.push("ENROLLMENT_PARTICIPATION_SIGNATURE_SCREENSHOT_PREVIEW_DECLINED");
            }
            pages.push("ENROLLMENT_DECLINED");
        }
        return pages;
    }
    get pages() {
        var _a;
        return (_a = this.enrollmentPages) !== null && _a !== void 0 ? _a : [];
    }
    get progressBarPercentage() {
        if (this.currentPageIndex === null || this.pages === NOT_LOADED)
            return 0;
        const pagesLength = this.isScreenshotPreviewMode
            ? this.pages.filter((page) => !this.checkIfInterimPage(page)).length
            : this.pages.length;
        const onePagePercentage = 100 / pagesLength;
        return (this.currentPageIndex + 1) * onePagePercentage;
    }
    get consentingPatientInfo() {
        var _a;
        if (this.authManager.userType === UserType.PATIENT) {
            const consentingPatientInfo = this.authManager.consentingPatientInfo;
            if (!consentingPatientInfo)
                return null;
            return {
                ...consentingPatientInfo,
                signedExternalId: this.signedExternalId,
            };
        }
        else if (this.isProvider &&
            this._session.guidedConsentPatientSearchResult !== NOT_LOADED &&
            this._session.guidedConsentPatientSearchResult) {
            const patient = this._session.guidedConsentPatientSearchResult.patient;
            if (!patient) {
                console.warn(`No patient info found`);
                return null;
            }
            const state = usaStateFromName(patient.state);
            let isUnderage = false;
            if (patient.birthDate) {
                const age = differenceInYears(new Date(), patient.birthDate);
                isUnderage = this.stateLawsService.isUnderage(state, age);
            }
            return {
                id: patient.healthExPatientId,
                mrn: patient.mrn,
                name: getFullName(patient),
                isUnderage,
                state,
                language: (_a = patient.language) !== null && _a !== void 0 ? _a : "en",
                signedExternalId: this.signedExternalId,
            };
        }
        return null;
    }
    get homePageAfterConsent() {
        if (this.isIAS)
            return NamedRoutes.PATIENT_HISTORY;
        return NamedRoutes.PATIENT_SHARING;
    }
    async reAuth() {
        this.authManager.logout();
        this.reset();
        await this.refreshStudyAndPatientInfo();
        this.goToPage("ENROLLMENT_LOGIN");
    }
    reset() {
        this.authManager.setPatientConsentOrgId(null);
        const consentFlowPageData = this._session._consentFlowPageData;
        const guidedConsentCloseRoute = this._session.guidedConsentCloseRoute;
        this._session = new PatientConsentSession();
        this._session._consentFlowPageData = consentFlowPageData;
        this._session.guidedConsentCloseRoute = guidedConsentCloseRoute;
        this.browserLanguageUIManager.setInMemoryLanguage(null);
        this.redirectingBeforeAPIs = false;
    }
    saveConsentSession() {
        const sessionSnapshot = instanceToPlain(this._session);
        this.persistence.setPatientConsentSession(sessionSnapshot);
        const inMemoryLanguage = this.browserLanguageUIManager.getInMemoryLanguage();
        this.persistence.setInMemoryLanguage(inMemoryLanguage);
    }
    restoreConsentSession() {
        const sessionSnapshot = this.persistence.getPatientConsentSession();
        if (sessionSnapshot) {
            const restored = plainToInstance(PatientConsentSession, sessionSnapshot);
            for (const key of Object.keys(sessionSnapshot)) {
                Reflect.set(this._session, key, Reflect.get(restored, key));
            }
            this.persistence.setPatientConsentSession(null);
        }
        const inMemoryLanguage = this.persistence.getInMemoryLanguage();
        if (inMemoryLanguage) {
            this.browserLanguageUIManager.setInMemoryLanguage(inMemoryLanguage);
            this.persistence.setInMemoryLanguage(null);
        }
    }
    resetRetrievalUIStateAndCompleteRestoration() {
        this.saveDocumentRetrievalUIState(null);
        this._session.patientConsentSessionState = PatientConsentSessionState.INITIALIZED;
    }
    clearRetrievalBatchId() {
        this._session.retrievalBatchId = null;
    }
    completeStateRestoration() {
        this._session.patientConsentSessionState = PatientConsentSessionState.INITIALIZED;
    }
    async restoreDocumentRetrievalState() {
        const batchId = this._session.retrievalBatchId;
        if (batchId) {
            const batch = await this.documentRetrievalManager.refreshBatch(batchId);
            if ((batch === null || batch === void 0 ? void 0 : batch.batchId) !== batchId) {
                console.warn(`Batch mismatch while restoring consent session`);
            }
        }
        else {
            console.warn(`No batch id found while restoring consent session`);
        }
    }
    async loginToClearAndSaveState(callbackHash, isSandbox, source, organizationId, initialScreen) {
        var _a;
        const nextPageRoute = (_a = this.getNextPageRoute()) !== null && _a !== void 0 ? _a : undefined;
        const pending = this.socialLoginAuthManager.pendingSocialLoginData;
        if ((pending === null || pending === void 0 ? void 0 : pending.verificationKey) && !pending.phoneVerified) {
            this.persistence.setConsentDeferredVerificationStash({
                verificationKey: pending.verificationKey,
                phoneNumber: pending.phoneNumber,
                gfidProvider: pending.gfidProvider,
            });
            this.persistence.setPendingSocialLoginSuccessMarker({
                provider: pending.gfidProvider,
                heapGlobalEventProperties: pending.heapGlobalEventProperties,
                timestamp: pending.socialLoginSuccessTimestamp,
            });
        }
        else {
            this.persistence.setConsentDeferredVerificationStash(null);
            this.persistence.setPendingSocialLoginSuccessMarker(null);
        }
        this.saveConsentSession();
        await this.clearAuthManager.redirectToClearLoginPage(callbackHash, source, organizationId, false, isSandbox, this.patientCollectedEmail, nextPageRoute, initialScreen);
    }
    async loginToLocationAndSaveState(locationKey, uiState) {
        var _a, _b, _c, _d;
        if (!this.retrieval)
            return;
        const location = this.retrieval.locations.find((loc) => loc.id === locationKey);
        if (!location) {
            console.warn(`Location not found to login to: ${locationKey}`);
            return;
        }
        if (location.authType === DocumentRetrievalAuthType.FASTEN) {
            if (((_a = location.organizationMetadata) === null || _a === void 0 ? void 0 : _a.authType) !== DocumentRetrievalAuthType.FASTEN) {
                console.warn(`No organization metadata`);
                return;
            }
            try {
                const batchId = this.retrieval.batchId;
                const brandId = location.organizationMetadata.brandId;
                const portalId = location.organizationMetadata.portalId;
                const endpointId = location.organizationMetadata.endpointId;
                const patientId = (_b = this.consentingPatientInfo) === null || _b === void 0 ? void 0 : _b.id;
                if (!brandId || !portalId || !endpointId) {
                    console.error("Fasten provider missing required reauth data:", { brandId, portalId, endpointId });
                    return;
                }
                if (!patientId) {
                    console.error("Patient ID is required for reauth");
                    return;
                }
                this.saveConsentSession();
                this.saveDocumentRetrievalUIState(instanceToPlain(uiState));
                await this.smartAuthManager.redirectToProviderPortalForFastenAuth(getFastenPublicId(this.authManager.isSandbox, this.environment), endpointId, brandId, portalId, patientId, locationKey);
            }
            catch (error) {
                console.warn(`Failed to get auth URL: ${error}`);
                await this.smartAuthManager.onSmartAuthError("Fasten redirect failed", SmartAuthErrorReason.AUTH_FAILED);
            }
        }
        else if (location.authType === DocumentRetrievalAuthType.FACILITATED_FHIR_EPIC) {
            if (((_c = location.organizationMetadata) === null || _c === void 0 ? void 0 : _c.authType) !== DocumentRetrievalAuthType.FACILITATED_FHIR_EPIC) {
                console.warn(`No organization metadata`);
                return;
            }
            try {
                const batchId = this.retrieval.batchId;
                const fhirEndpoint = location.organizationMetadata.fhirEndpoint;
                const authType = location.authType;
                const epicIntegrationForAuth = (_d = location.organizationMetadata.epicIntegration) !== null && _d !== void 0 ? _d : EpicIntegration.EPIC_NEXUS;
                const redirectUrl = this.facilitatedFHIRAuthManager.getRedirectUri(epicIntegrationForAuth);
                const urlAndState = await this.documentRetrievalManager.getFacilitatedFHIRAuthUrl(batchId, fhirEndpoint, redirectUrl, authType, epicIntegrationForAuth);
                if (!urlAndState) {
                    console.warn(`Failed to get auth URL`);
                    this.facilitatedFHIRAuthManager.handleAuthError(FacilitatedFHIRAuthErrorReason.AUTH_FAILED, locationKey);
                    return;
                }
                this.saveConsentSession();
                this.saveDocumentRetrievalUIState(instanceToPlain(uiState));
                this.facilitatedFHIRAuthManager.redirectToFacilitatedFHIRLoginPage(locationKey, urlAndState.url, urlAndState.state, window.location.hash);
            }
            catch (error) {
                console.warn(`Failed to get auth URL: ${error}`);
                this.facilitatedFHIRAuthManager.handleAuthError(FacilitatedFHIRAuthErrorReason.AUTH_FAILED, locationKey);
            }
        }
        else {
            console.warn(`Unexpected auth type: ${location.authType}`);
        }
    }
    async handleFacilitatedFHIRAuthSuccess(key, code) {
        var _a, _b;
        try {
            if (!this.retrieval) {
                console.warn("No retrieval found");
                return;
            }
            const location = this.retrieval.locations.find((loc) => loc.id === key);
            if (!location) {
                console.warn(`Location not found: ${key}`);
                return;
            }
            if (((_a = location.organizationMetadata) === null || _a === void 0 ? void 0 : _a.authType) !== DocumentRetrievalAuthType.FACILITATED_FHIR_EPIC) {
                console.warn(`No organization metadata`);
                return;
            }
            const epicIntegrationForRecords = (_b = location.organizationMetadata.epicIntegration) !== null && _b !== void 0 ? _b : EpicIntegration.EPIC_NEXUS;
            const result = await this.documentRetrievalManager.getFacilitatedFHIRRecords(this.retrieval.batchId, location.id, location.organizationMetadata.fhirEndpoint, location.authType, this.facilitatedFHIRAuthManager.getRedirectUri(epicIntegrationForRecords), code, epicIntegrationForRecords);
            if (isErr(result) && result.error.reason === DocumentRetrievalFailureReason.INSUFFICIENT_SCOPE) {
                this.facilitatedFHIRAuthManager.handleAuthError(FacilitatedFHIRAuthErrorReason.INSUFFICIENT_SCOPE, key);
            }
        }
        catch (error) {
            console.error("Error handling facilitated FHIR auth success: ", error);
        }
    }
    async processClearLoginResultIfNeeded() {
        var _a, _b, _c, _d;
        const isValidRoute = [
            NamedRoutes.PATIENT_CONSENT_ENROLLMENT.routeName,
            NamedRoutes.PATIENT_CONSENT_DATA_UPDATE.routeName,
            NamedRoutes.PATIENT_IAS_CONSENT.routeName,
            NamedRoutes.PATIENT_IAS_DATA_UPDATE.routeName,
        ].includes((_b = (_a = this.routingUIManager.currentRoute) === null || _a === void 0 ? void 0 : _a.routeName) !== null && _b !== void 0 ? _b : "");
        const isValidStep = ((_c = this.routingUIManager.currentRouteParams) === null || _c === void 0 ? void 0 : _c.step) === "clear-upgrade" ||
            ((_d = this.routingUIManager.currentRouteParams) === null || _d === void 0 ? void 0 : _d.step) === "verify-otp";
        const clearAuthData = this.clearAuthManager.getClearAuthData();
        if (clearAuthData && this.clearAuthManager.hasCompletedAuth && isValidRoute && isValidStep) {
            this.restoreConsentSession();
            this._session.patientConsentSessionState = PatientConsentSessionState.RESTORING;
            await this.refreshIAL2Info();
            this._session.patientConsentSessionState = PatientConsentSessionState.RESTORING_COMPLETED;
        }
        else if ((clearAuthData === null || clearAuthData === void 0 ? void 0 : clearAuthData.initialScreen) === ClearInitialScreen.PATIENT_CONSENT_CLEAR_UPGRADE &&
            !this.clearAuthManager.hasCompletedAuth &&
            isValidRoute) {
            this.restoreConsentSession();
            this.clearAuthManager.resetAuth();
        }
    }
    processFacilitatedFHIRAuthState() {
        if (this.facilitatedFHIRAuthManager.hasCompletedAuth) {
            this.restoreConsentSession();
            this._session.patientConsentSessionState = PatientConsentSessionState.RESTORING;
        }
        if (this.facilitatedFHIRAuthManager.state.state === FacilitatedFHIRAuthState.SUCCESS) {
            const { key, code } = this.facilitatedFHIRAuthManager.state;
            void this.restoreDocumentRetrievalState()
                .then(() => this.handleFacilitatedFHIRAuthSuccess(key, code))
                .then(() => {
                this._session.patientConsentSessionState = PatientConsentSessionState.RESTORING_COMPLETED;
            })
                .catch((error) => {
                console.error("Error handling facilitated FHIR auth success: ", error);
            });
        }
        else if (this.facilitatedFHIRAuthManager.state.state === FacilitatedFHIRAuthState.ERROR) {
            void this.restoreDocumentRetrievalState().then(() => {
                this._session.patientConsentSessionState = PatientConsentSessionState.RESTORING_COMPLETED;
            });
        }
    }
    async saveStateForAuthSession(uiState) {
        this.saveConsentSession();
        this.saveDocumentRetrievalUIState(instanceToPlain(uiState));
    }
    restoreStateForAuthCallback() {
        const smartAuthData = this.smartAuthManager.getSmartAuthData();
        if ((smartAuthData === null || smartAuthData === void 0 ? void 0 : smartAuthData.provider) === SmartAuthProvider.FASTEN && this.smartAuthManager.hasCompletedAuth) {
            this.restoreConsentSession();
            this._session.patientConsentSessionState = PatientConsentSessionState.RESTORING;
            void this.restoreDocumentRetrievalState()
                .then(() => (this._session.patientConsentSessionState = PatientConsentSessionState.RESTORING_COMPLETED))
                .catch((error) => {
                console.error("Error handling smart auth state: ", error);
            });
        }
    }
    saveDocumentRetrievalUIState(uiState) {
        this.persistence.setDocumentRetrievalUIState(uiState);
    }
    getDocumentRetrievalUIState() {
        return this.persistence.getDocumentRetrievalUIState();
    }
    getConsentPagesViewed() {
        if (this.enrollmentPages === NOT_LOADED)
            return NOT_LOADED;
        if (!this.enrollmentPages)
            return null;
        let pagesViewed = [...this.enrollmentPages];
        pagesViewed = pagesViewed.filter((page) => !PDDA_SUB_STEP_ROUTES.includes(page));
        const signaturePageIndex = pagesViewed.indexOf("ENROLLMENT_PARTICIPATION_SIGNATURE");
        if (signaturePageIndex !== -1) {
            pagesViewed = pagesViewed.slice(0, signaturePageIndex + 1);
        }
        else {
            console.warn("No signature page found in consent pages viewed, returning no pages");
            pagesViewed = [];
        }
        return pagesViewed;
    }
    async authorizeConsent(isConsented, input, allowNavigationAfterConsentConfirmation = true) {
        if (this.consentExpirationDate === null) {
            return;
        }
        if (!this.consentingPatientInfo) {
            return;
        }
        const pagesViewed = this.getConsentPagesViewed();
        if (pagesViewed === NOT_LOADED) {
            return;
        }
        this._session.isSubmittingConsent = true;
        this._session.hasConsentBeenSubmittedThisFlow = true;
        const consentInput = isConsented
            ? {
                expirationDate: this.consentExpirationDate,
                consentUIFlow: ConsentUIFlow.PATIENT_DIRECTED_DATA_EXCHANGE,
                uiHash: getUIHash(),
                pagesViewed,
                consentedByLink: this.consentedByLink,
                providerId: this.providerId,
                ...input,
            }
            : {
                expirationDate: this.consentExpirationDate,
                consentedByLink: this.consentedByLink,
                providerId: this.providerId,
                ...input,
            };
        try {
            if (isConsented) {
                if (!this.isInteractivePreview && !this.isScreenshotMode) {
                    if (this.isIAS) {
                        await this.iasManager.optInToIAS(this.consentingPatientInfo, consentInput);
                    }
                    else {
                        if (this._session.study === NOT_LOADED || this._session.study === null) {
                            throw new Error(`Unsupported no study for enrollment flow`);
                        }
                        await this.studyManager.optInToStudy(this.consentingPatientInfo, this._session.study, getConsentTypesForProject(this._session.study, !!this.allowsIdentifiedFutureDataUse), consentInput);
                    }
                    this._session.hasAuthorizedConsentEnrollment = true;
                    if (allowNavigationAfterConsentConfirmation)
                        this.goToNextPage();
                }
            }
            else {
                if (!this.isInteractivePreview && !this.isScreenshotMode) {
                    if (this.isIAS) {
                        await this.iasManager.optOutOfIAS(this.consentingPatientInfo, consentInput);
                    }
                    else {
                        if (this._session.study === NOT_LOADED || this._session.study === null) {
                            throw new Error(`Unsupported no study for enrollment flow`);
                        }
                        await this.studyManager.optOutOfStudy(this.consentingPatientInfo, this._session.study, getConsentTypesForProject(this._session.study, !!this.allowsIdentifiedFutureDataUse), consentInput);
                    }
                }
                this._session.hasAuthorizedConsentEnrollment = false;
                this.goToPage("ENROLLMENT_DECLINED");
            }
        }
        finally {
            this._session.isSubmittingConsent = false;
        }
    }
    _close(route) {
        const studyId = this._session.studyId;
        const isPreview = this.isInteractivePreview;
        const isProvider = this.isProvider;
        const guidedConsentPatientMRN = this._session.guidedConsentPatientMRN;
        const guidedConsentCloseRoute = this._session.guidedConsentCloseRoute;
        this.reset();
        if (!isPreview) {
            if (isProvider) {
                if (guidedConsentPatientMRN) {
                    if ((guidedConsentCloseRoute === null || guidedConsentCloseRoute === void 0 ? void 0 : guidedConsentCloseRoute.routeName) === NamedRoutes.PATIENTS_STUDY_DETAILS.routeName && studyId) {
                        this.routingUIManager.navigateTo(NamedRoutes.PATIENTS_STUDY_DETAILS, {
                            studyId,
                            mrn: guidedConsentPatientMRN,
                        });
                    }
                    else {
                        this.routingUIManager.navigateTo(NamedRoutes.PATIENTS_SEARCH, {
                            mrn: guidedConsentPatientMRN,
                        });
                    }
                }
                else {
                    this.routingUIManager.navigateTo(NamedRoutes.PATIENTS);
                }
            }
            else if (route === NamedRoutes.PATIENT_SHARING) {
                this.patientResearchDashboardUIManager.goToPage(MobileBottomNavigationButtonType.RESEARCH);
            }
            else {
                this.routingUIManager.navigateTo(route);
            }
        }
    }
    close() {
        this._close(this.homePageAfterConsent);
    }
    done() {
        this.persistence.setIdpHintForAIFlow(null);
        this.persistence.setRedirectUriForHostedFlow(null);
        this.persistence.setIss(null);
        this.persistence.setSignedExternalId(null);
        this._close(this.homePageAfterConsent);
    }
    goToPage(route, options) {
        if (this.isInteractivePreview) {
            this._session.currentInteractivePreviewRoute = route;
        }
        else {
            const result = this.patientConsentManagerRouteToRoute(route);
            const routeParams = {
                ...result.routeParams,
                ...((options === null || options === void 0 ? void 0 : options.link) !== undefined ? { link: options.link } : {}),
                ...((options === null || options === void 0 ? void 0 : options.locationIndex) !== undefined ? { locationIndex: options.locationIndex } : {}),
            };
            if (routeParams.locationIndex !== undefined && !routeParams.link) {
                routeParams.link = "-";
            }
            if (options === null || options === void 0 ? void 0 : options.replace) {
                this.routingUIManager.redirectTo(result.route, routeParams);
            }
            else {
                this.routingUIManager.navigateTo(result.route, routeParams);
            }
            if (!CONSENT_FLOW_TERMINAL_ROUTES.includes(route)) {
                void this.saveConsentFlowProgress(consentFlowProgressMilestoneForRoute(route), { route });
            }
        }
    }
    get currentRouteLocationIndex() {
        var _a, _b;
        if (this.isInteractivePreview)
            return null;
        return (_b = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.locationIndex) !== null && _b !== void 0 ? _b : null;
    }
    goToPreviousPage() {
        if (this.currentPageIndex === null || this.pages === NOT_LOADED) {
            return;
        }
        if (this.currentPageIndex <= 0) {
            this.close();
            return;
        }
        let targetPageIndex = this.currentPageIndex - 1;
        const validPages = [];
        const invalidPages = [];
        if (this.isPDDAGeneralDataUse) {
            if (this.pages[this.currentPageIndex] === "ENROLLMENT_DECLINED") {
                validPages.push("ENROLLMENT_WELCOME");
            }
            else {
                invalidPages.push("ENROLLMENT_REQUEST_DETAILS_POPUP");
                if (this.isPatientLoggedIn) {
                    invalidPages.push("ENROLLMENT_LOGIN");
                }
            }
        }
        if (PDDA_SUB_STEP_ROUTES.includes(this.pages[this.currentPageIndex])) {
            invalidPages.push(...PDDA_ALL_ROUTES);
        }
        if (invalidPages.length > 0) {
            while (invalidPages.includes(this.pages[targetPageIndex])) {
                if (targetPageIndex <= 0) {
                    break;
                }
                targetPageIndex -= 1;
            }
        }
        if (validPages.length > 0) {
            while (!validPages.includes(this.pages[targetPageIndex])) {
                if (targetPageIndex <= 0) {
                    break;
                }
                targetPageIndex -= 1;
            }
        }
        this.goToPage(this.pages[targetPageIndex]);
    }
    getResumePageAfterClearLogin() {
        var _a, _b;
        if (this.pages === NOT_LOADED)
            return null;
        const clearIdx = this.pages.indexOf("ENROLLMENT_CLEAR_UPGRADE");
        if (clearIdx >= 0 && clearIdx < this.pages.length - 1) {
            return (_a = this.pages[clearIdx + 1]) !== null && _a !== void 0 ? _a : null;
        }
        const loginIdx = this.pages.indexOf("ENROLLMENT_LOGIN");
        if (loginIdx >= 0 && loginIdx < this.pages.length - 1) {
            return (_b = this.pages[loginIdx + 1]) !== null && _b !== void 0 ? _b : null;
        }
        return null;
    }
    isRouteInPages(route) {
        if (this.pages === NOT_LOADED)
            return false;
        return this.pages.includes(route);
    }
    getNextPageRoute() {
        var _a;
        if (this.currentPageIndex === null || this.currentPageIndex < 0 || this.pages === NOT_LOADED)
            return null;
        if (this.currentPageIndex >= this.pages.length - 1)
            return null;
        let targetPageIndex = this.currentPageIndex + 1;
        if (this.isPDDAGeneralDataUse) {
            while (this.pages[targetPageIndex] === "ENROLLMENT_REQUEST_DETAILS_POPUP") {
                if (targetPageIndex === this.pages.length - 1)
                    break;
                targetPageIndex += 1;
            }
        }
        if (this.currentPage !== null && PDDA_ALL_ROUTES.includes(this.currentPage)) {
            while (targetPageIndex < this.pages.length && PDDA_ALL_ROUTES.includes(this.pages[targetPageIndex])) {
                targetPageIndex += 1;
            }
        }
        return (_a = this.pages[targetPageIndex]) !== null && _a !== void 0 ? _a : null;
    }
    goToNextPage() {
        if (this.currentPageIndex === null || this.pages === NOT_LOADED) {
            return;
        }
        if (this.currentPageIndex === this.pages.length - 1) {
            if (this.isProvider) {
                this.routingUIManager.navigateTo(NamedRoutes.PATIENTS);
            }
            else {
                this.routingUIManager.navigateTo(NamedRoutes.PATIENT_SHARING);
            }
            return;
        }
        const nextPage = this.getNextPageRoute();
        if (nextPage) {
            if ((this.currentPage === "ENROLLMENT_LOGIN" && this.isPatientLoggedIn) || this.currentPage === "ENROLLMENT_PDDA_HUR_LOADING") {
                this.goToPage(nextPage, { replace: true });
            }
            else {
                this.goToPage(nextPage);
            }
        }
        else {
            console.error("No next page found while going to next page");
        }
    }
    refreshPage() {
        if (this.currentPage !== null)
            this.goToPage(this.currentPage);
    }
    showPopup(route) {
        this._session.routeBeforePopup = this.currentPatientConsentRoute;
        this.goToPage(route);
    }
    hidePopupIfPresent() {
        if (!this._session.routeBeforePopup)
            return false;
        const routeToReturnTo = this._session.routeBeforePopup;
        this._session.routeBeforePopup = null;
        this.goToPage(routeToReturnTo);
        return true;
    }
    patientConsentManagerRouteToRoute(route) {
        const step = patientConsentRouteStep(route);
        const link = this.pathLink ? slugify(this.pathLink) : undefined;
        if (this._session.guidedConsentPatientMRN) {
            return {
                route: NamedRoutes.GUIDED_PATIENT_CONSENT_ENROLLMENT,
                routeParams: {
                    studyId: this._session.studyId,
                    mrn: this._session.guidedConsentPatientMRN,
                    step,
                    ...(this._session.guidedConsentLink ? { link: "link" } : {}),
                },
            };
        }
        else if (this.isIAS) {
            if (this.isDataUpdate) {
                return {
                    route: NamedRoutes.PATIENT_IAS_DATA_UPDATE,
                    routeParams: { step, link },
                };
            }
            else {
                return {
                    route: NamedRoutes.PATIENT_IAS_CONSENT,
                    routeParams: { step, link },
                };
            }
        }
        else if (this.isDataUpdate) {
            return {
                route: NamedRoutes.PATIENT_CONSENT_DATA_UPDATE,
                routeParams: { studyId: this._session.studyId, step, link },
            };
        }
        else {
            return {
                route: NamedRoutes.PATIENT_CONSENT_ENROLLMENT,
                routeParams: { studyId: this._session.studyId, step, link },
            };
        }
    }
    routeToPatientConsentRoute(route, routeParams) {
        if (!route) {
            return null;
        }
        const step = routeParams === null || routeParams === void 0 ? void 0 : routeParams.step;
        if (!step) {
            return null;
        }
        if (route.routeName === NamedRoutes.PATIENT_CONSENT_ENROLLMENT.routeName ||
            route.routeName === NamedRoutes.GUIDED_PATIENT_CONSENT_ENROLLMENT.routeName ||
            route.routeName === NamedRoutes.PATIENT_CONSENT_DATA_UPDATE.routeName ||
            route.routeName === NamedRoutes.PATIENT_IAS_CONSENT.routeName ||
            route.routeName === NamedRoutes.PATIENT_IAS_DATA_UPDATE.routeName) {
            const patientConsentManagerRoute = patientConsentRouteFromStep(unslugify(step));
            if (patientConsentManagerRoute)
                return patientConsentManagerRoute;
        }
        return null;
    }
    startFromBeginning() {
        this.goToPage("ENROLLMENT_WELCOME");
    }
    async saveConsentFlowProgress(milestone, options) {
        var _a, _b, _c, _d, _e;
        const route = (_a = options === null || options === void 0 ? void 0 : options.route) !== null && _a !== void 0 ? _a : this.currentPatientConsentRoute;
        if (route !== null && CONSENT_FLOW_TERMINAL_ROUTES.includes(route)) {
            return;
        }
        const patientId = (_c = (_b = this.consentingPatientInfo) === null || _b === void 0 ? void 0 : _b.id) !== null && _c !== void 0 ? _c : null;
        if (!patientId) {
            if (this.patientCollectedEmail) {
                await this.saveConsentFlowProgressByEmail(milestone !== null && milestone !== void 0 ? milestone : {});
                return;
            }
            return;
        }
        const update = {
            studyId: (_d = this._session.studyId) !== null && _d !== void 0 ? _d : null,
            studyVersionId: (_e = this.studyVersionId) !== null && _e !== void 0 ? _e : null,
            consentedByLink: this.consentedByLink,
            ...(this.patientCollectedEmail ? { email: this.patientCollectedEmail } : {}),
            ...milestone,
        };
        try {
            await this.patientService.updateConsentFlowProgress(patientId, update);
        }
        catch (e) {
            console.warn("Error saving consent flow progress", e);
        }
    }
    async saveConsentFlowProgressByEmail(milestone) {
        var _a, _b;
        if (!this.patientCollectedEmail) {
            return;
        }
        const update = {
            studyId: (_a = this._session.studyId) !== null && _a !== void 0 ? _a : null,
            studyVersionId: (_b = this.studyVersionId) !== null && _b !== void 0 ? _b : null,
            email: this.patientCollectedEmail,
            consentedByLink: this.consentedByLink,
            ...milestone,
        };
        try {
            await this.patientService.updateConsentFlowProgressByEmail(update);
        }
        catch (e) {
            console.warn("Error saving consent flow progress by email", e);
        }
    }
    async deleteConsentFlowProgress() {
        var _a, _b, _c;
        const patientId = (_b = (_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null;
        if (!patientId) {
            return;
        }
        const studyId = (_c = this._session.studyId) !== null && _c !== void 0 ? _c : null;
        try {
            await this.patientService.deleteConsentFlowProgress(patientId, { studyId });
        }
        catch (e) {
            console.warn("Error deleting consent flow progress", e);
        }
    }
    updatePatientInformedConsentEnabled(isConsented, isInterested) {
        this._session.patientInformedConsentEnabled = isConsented;
        this._session.isInterested = isInterested;
    }
    updateUserSignatureHIPAA(patientSignatureHIPAA) {
        this._session.patientSignatureHIPAA = patientSignatureHIPAA;
    }
    updateUserSigningReasonHIPAA(signingReason) {
        this._session.patientSigningReasonHIPAA = signingReason;
    }
    updateProviderSignature(providerSignature) {
        this._session.providerSignature = providerSignature;
    }
    updateParentGuardianSignature(parentGuardianSignature) {
        this._session.parentGuardianSignature = parentGuardianSignature;
    }
    updateParentGuardianSigningReason(parentGuardianSigningReason) {
        this._session.parentGuardianSigningReason = parentGuardianSigningReason;
    }
    updateAdditionalParentGuardianSignature(additionalParentGuardianSignature) {
        this._session.additionalParentGuardianSignature = additionalParentGuardianSignature;
    }
    updateAdditionalParentGuardianSigningReason(additionalParentGuardianSigningReason) {
        this._session.additionalParentGuardianSigningReason = additionalParentGuardianSigningReason;
    }
    updateMinorParticipantSignature(minorParticipantSignature) {
        this._session.minorParticipantSignature = minorParticipantSignature;
    }
    updateWitnessSignature(witnessSignature) {
        this._session.witnessSignature = witnessSignature;
    }
    updateDocumentRetrievalLocations(documentRetrievalLocations) {
        this._session.documentRetrievalLocations = documentRetrievalLocations;
    }
    updateLocationsNeedingAuthentication(locationsNeedingAuthentication) {
        this._session.locationsNeedingAuthentication = locationsNeedingAuthentication;
    }
    updateContactMethod(contactMethod, contactEmail, contactPhone) {
        this._session.contactMethod = contactMethod;
        this._session.contactEmail = contactEmail;
        this._session.contactPhone = contactPhone;
    }
    setHasLoggedInDuringConsent() {
        this._session.hasLoggedInDuringConsent = true;
    }
    async declineConsent() {
        if (this.isInConsentFlow) {
            this.updatePatientInformedConsentEnabled(false, null);
        }
        if (this.isInteractivePreview || this.isScreenshotPreviewMode || this.isScreenshotMode || this.isDataUpdate) {
            this.goToPage("ENROLLMENT_DECLINED");
            return;
        }
        if (this.isPatientLoggedIn) {
            await this.authorizeConsent(false, {});
        }
        else {
            this.goToPage("ENROLLMENT_DECLINED");
        }
    }
    get useTefca() {
        var _a, _b;
        return !this.authManager.blockedFromUsingIAL2 && ((_b = (_a = this.getIAL2Info()) === null || _a === void 0 ? void 0 : _a.ial2Authorized) !== null && _b !== void 0 ? _b : false);
    }
    async initiateDocumentRetrieval(studyId) {
        const sessionBeforeAwait = this._session;
        const batchId = await this.documentRetrievalManager.initiateDocumentRetrieval(studyId, this.useTefca);
        if (this._session !== sessionBeforeAwait)
            return;
        this.applyRetrievalBatchToSession(batchId);
    }
    async restoreExistingInProgressRetrievalIfAny() {
        const sessionBeforeAwait = this._session;
        const existing = await this.documentRetrievalManager.getExistingInProgressDocumentRetrieval();
        if (this._session !== sessionBeforeAwait)
            return null;
        if (!existing)
            return null;
        this.applyRetrievalBatchToSession(existing.batchId);
        return existing;
    }
    applyRetrievalBatchToSession(batchId) {
        var _a, _b;
        this._session.retrievalBatchId = batchId;
        this.updateLocationsNeedingAuthentication((_b = (_a = this.documentRetrievalManager.retrieval) === null || _a === void 0 ? void 0 : _a.locations.filter((location) => location.status === DocumentRetrievalLocationStatus.NEEDS_AUTH).map((location) => location.id)) !== null && _b !== void 0 ? _b : []);
    }
    async confirmConsent(allowNavigationAfterConsentConfirmation) {
        var _a, _b;
        if (this.isInConsentFlow) {
            this.updatePatientInformedConsentEnabled(true, null);
        }
        const consentInput = {
            patientName: this.patientName,
            patientMRN: this.patientMRN,
            signerName: this._session.patientSignatureHIPAA,
            signingReason: this._session.patientSigningReasonHIPAA,
            parentGuardianSignerName: this._session.parentGuardianSignature,
            parentGuardianSigningReason: this._session.parentGuardianSigningReason,
            additionalParentGuardianSignerName: this._session.additionalParentGuardianSignature,
            additionalParentGuardianSigningReason: this._session.additionalParentGuardianSigningReason,
            additionalParentGuardianExemptSignature: this._session.additionalParentGuardianExemptSignature,
            minorParticipantSignerName: this._session.minorParticipantSignature,
            minorParticipantExemptFromAssent: this._session.minorParticipantExemptFromAssent,
            witnessSkipSignature: this._session.witnessSkipSignature,
            witnessSignerName: this._session.witnessSignature,
            isProxyAccess: this.isProxyAccess,
            providerSignerName: this.isProvider ? this.providerName : null,
            mp4Url: (_b = (_a = this.videoTranscode) === null || _a === void 0 ? void 0 : _a.mp4Url) !== null && _b !== void 0 ? _b : null,
            posterUrl: getPosterUrl(this.videoTranscode),
            loggedInDuringConsent: this._session.hasLoggedInDuringConsent,
        };
        await this.authorizeConsent(true, consentInput, allowNavigationAfterConsentConfirmation);
    }
    async confirmImInterested() {
        if (this.isInteractivePreview || this.isScreenshotPreviewMode || this.isScreenshotMode) {
            this.goToNextPage();
            return;
        }
        if (!this._session.studyId)
            return;
        if (!this.consentingPatientInfo)
            return;
        await this.studyManager.updateIdentifiedPatientImInterested(this._session.studyId, this.consentingPatientInfo);
        this.goToNextPage();
    }
    startConsentFlow(studyId, mrn, link) {
        this.reset();
        this._session.guidedConsentPatientMRN = mrn;
        this._session.studyId = studyId;
        this._session.guidedConsentLink = link;
        this.goToPage("ENROLLMENT_WELCOME");
    }
    dataUpdateConsentFlow(studyId) {
        this.routingUIManager.navigateTo(NamedRoutes.PATIENT_CONSENT_DATA_UPDATE, { studyId });
    }
    getConsentExpirationFromStudy(study) {
        return subDays(this.getExactConsentExpirationFromStudy(study), 1);
    }
    getExactConsentExpirationFromStudy(study) {
        var _a;
        return this.consentExpirationService.getConsentExpiration(study.completionDate, (_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.state);
    }
    getConsentFlowWaitForSelector(step) {
        return {
            selector: [
                { selector: `#${patientConsentStepParentId}`, strategy: ScreenshotWaitStrategy.WAIT_FOR_SELECTOR },
                { selector: `.${screenshotResourceLoadingClass}`, strategy: ScreenshotWaitStrategy.WAIT_FOR_ALL_REMOVED },
            ],
        };
    }
    getConsentFlowScreenshotHeight(step) {
        if (this.checkIfInterimPage(step))
            return 50;
        return undefined;
    }
    async getConsentFlowUrls(studyId, studyVersionId, previewMetadata, pagesViewed) {
        const rootURL = `${window.location.origin}/#/patient-consent/${studyId !== null && studyId !== void 0 ? studyId : IAS_PATH}`;
        let pages;
        if (this.isScreenshotPreviewMode && previewMetadata) {
            await this.startInteractivePreview(studyId !== null && studyId !== void 0 ? studyId : "", studyVersionId !== null && studyVersionId !== void 0 ? studyVersionId : "", previewMetadata);
            const previewPages = this.pages;
            if (previewPages !== NOT_LOADED)
                pages = previewPages;
        }
        else {
            pages = pagesViewed
                ?
                    pagesViewed.filter((pageViewed) => includes(patientConsentRouteValues, pageViewed))
                : patientConsentRouteValues;
        }
        return (pages !== null && pages !== void 0 ? pages : []).map((route) => {
            const step = patientConsentRouteStep(route);
            const waitForInfo = this.getConsentFlowWaitForSelector(route);
            return {
                name: route,
                url: `${rootURL}/enrollment/${slugify(step)}`,
                waitForSelector: waitForInfo.selector,
                waitStrategy: waitForInfo.strategy,
                height: this.getConsentFlowScreenshotHeight(route),
            };
        });
    }
    reconsent() {
        if (this.pages !== NOT_LOADED) {
            const pages = this.pages;
            const lastExcludedIndex = Math.max(-1, ...this.excludedPagesForReconsent.map((page) => pages.indexOf(page)));
            const firstPageForReconsent = pages[lastExcludedIndex + 1];
            if (firstPageForReconsent) {
                this.goToPage(firstPageForReconsent);
            }
        }
    }
    async trackEvent(event) {
        var _a, _b, _c, _d, _e;
        if (this.isInteractivePreview)
            return;
        let category;
        if (this._session.study !== NOT_LOADED && this._session.study) {
            const consentType = getPrimaryConsentTypeForProject(this._session.study);
            category = mapConsentTypeToEventCategory(consentType);
        }
        else {
            category = EventCategory.PATIENT_DIRECTED_DATA_EXCHANGE;
        }
        const metadata = {
            userType: this.isProvider ? "PROVIDER" : UserType.PATIENT,
            flowType: this.isProvider ? EnrollmentFlowType.GUIDED : EnrollmentFlowType.SELF_SERVE,
            userId: (_c = (_b = (_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : this.authManager.id) !== null && _c !== void 0 ? _c : "",
            page: (_d = this.currentPage) !== null && _d !== void 0 ? _d : undefined,
        };
        await this.analyticsService.trackEvent({
            studyId: this._session.studyId,
            category: (_e = event.category) !== null && _e !== void 0 ? _e : category,
            action: event.action,
            metadata,
        });
    }
    previewMetadataRepresentsConsentFlow(previewConsentFlow) {
        if (!previewConsentFlow)
            return false;
        if (previewConsentFlow === PreviewConsentFlow.PATIENT_DIRECTED_DATA_EXCHANGE)
            return true;
        return willNeverHappen(previewConsentFlow);
    }
    reactiveRedirect(route, handler) {
        reaction(() => [this.routingUIManager.currentRoute, this.routingUIManager.currentRouteParams], async ([currentRoute, _currentRouteParams]) => {
            if (!routeEquals(currentRoute, route))
                return;
            if (!this.routingUIManager.currentRouteParams)
                return;
            const result = await handler(this.routingUIManager.currentRouteParams);
            if (result) {
                this.routingUIManager.redirectTo(result.route, result.routeParams);
            }
        });
    }
    get urlParams() {
        const queryParams = effectiveQueryParams();
        const oauthState = queryParams.get("oauth_state");
        const idpHint = queryParams.get("idp_hint");
        return { oauthState, idpHint };
    }
    handleUrlParams() {
        this.restoreOAuthParamsFromStorage();
        if (this.urlParams.idpHint) {
            this.persistence.setIdpHintForAIFlow(this.urlParams.idpHint);
        }
        try {
            if (!this.urlParams.oauthState) {
                console.log("No oauthState in URL, keeping existing oauthParams:", this.oauthParams);
                return;
            }
            const decodedState = atob(this.urlParams.oauthState);
            this.oauthParams = JSON.parse(decodedState);
            this.persistOAuthParamsToStorage();
        }
        catch (e) {
            console.error("Failed to parse OAuth state:", e);
            if (!this.oauthParams) {
                this.oauthParams = null;
            }
        }
    }
    persistOAuthParamsToStorage() {
        if (this.oauthParams) {
            try {
                localStorage.setItem(this.oauthParamsKey, JSON.stringify(this.oauthParams));
            }
            catch (e) {
                console.warn("Failed to persist OAuth params to localStorage:", e);
            }
        }
    }
    restoreOAuthParamsFromStorage() {
        try {
            const stored = localStorage.getItem(this.oauthParamsKey);
            if (stored) {
                this.oauthParams = JSON.parse(stored);
            }
        }
        catch (e) {
            console.warn("Failed to restore OAuth params from localStorage:", e);
            this.oauthParams = null;
        }
    }
    clearOAuthParamsFromStorage() {
        try {
            localStorage.removeItem(this.oauthParamsKey);
        }
        catch (e) {
            console.warn("Failed to clear OAuth params from localStorage:", e);
        }
    }
    get isAIFlow() {
        if (this.studyInfo === NOT_LOADED || this.studyInfo === null)
            return false;
        return this.studyInfo.projectType === ProjectType.AI_DATA_USE;
    }
    get isOAuthFlow() {
        return this.oauthParams !== null && this.isAIFlow;
    }
    get idpHint() {
        return this.persistence.getIdpHintForAIFlow();
    }
    get signedExternalId() {
        return this.persistence.getSignedExternalId();
    }
    get iss() {
        return this.persistence.getIss();
    }
    get allowedHostedRedirectUrls() {
        var _a;
        if (this.studyInfo === NOT_LOADED || this.studyInfo === null) {
            return [];
        }
        return (_a = this.studyInfo.hostedRedirectUrls) !== null && _a !== void 0 ? _a : [];
    }
    normalizeUrl(url) {
        try {
            const parsed = new URL(url);
            const normalizedPath = `${withoutTrailingSlash(parsed.pathname)}/`;
            return `${parsed.protocol}//${parsed.host.toLowerCase()}${normalizedPath}${parsed.search}`;
        }
        catch (e) {
            console.warn("[HostedFlow] Invalid URL:", url, e);
            return null;
        }
    }
    isRedirectUriAllowed(redirectUri) {
        const normalizedInput = this.normalizeUrl(redirectUri);
        if (!normalizedInput)
            return false;
        return this.allowedHostedRedirectUrls.some((allowedUrl) => {
            if (allowedUrl.endsWith("/*")) {
                const baseUrl = allowedUrl.slice(0, -1);
                const normalizedBase = this.normalizeUrl(baseUrl);
                if (!normalizedBase)
                    return false;
                return normalizedInput.startsWith(normalizedBase);
            }
            const normalizedAllowed = this.normalizeUrl(allowedUrl);
            return normalizedAllowed === normalizedInput;
        });
    }
    get hostedRedirectUrl() {
        const queryParams = effectiveQueryParams();
        const urlRedirectUri = queryParams.get("redirectUri");
        const redirectUri = urlRedirectUri !== null && urlRedirectUri !== void 0 ? urlRedirectUri : this.persistence.getRedirectUriForHostedFlow();
        if (!redirectUri)
            return null;
        if (this.isRedirectUriAllowed(redirectUri)) {
            return redirectUri;
        }
        console.warn("[HostedFlow] Redirect URI not in allowed list:", redirectUri);
        return null;
    }
    get hasHostedRedirectUrl() {
        return !!this.hostedRedirectUrl;
    }
    get canRedirectExternally() {
        return this.isOAuthFlow || this.hasHostedRedirectUrl;
    }
    async updatePatient(input) {
        if (!this.authManager.id) {
            console.error("No patient ID available for update");
            return false;
        }
        return await this.patientService.updatePatient(this.authManager.id, input);
    }
    async completeOAuthAfterPDDE() {
        var _a, _b;
        if (!this.isOAuthFlow) {
            console.warn("No OAuth params available for completion");
            return;
        }
        const oauthParams = { ...this.oauthParams };
        try {
            this.clearOAuthParamsFromStorage();
            this.oauthParams = null;
            this.persistence.setIdpHintForAIFlow(null);
            this.persistence.setSignedExternalId(null);
            const queryParams = {
                ...oauthParams,
                userId: (_b = (_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : this.authManager.id,
                organizationId: this.authManager.patientConsentOrgId,
                claims: {
                    firstName: this.authManager.firstName,
                    lastName: this.authManager.lastName,
                    state: this.authManager.state,
                },
            };
            const camelToSnakeCase = (str) => str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
            const filteredQueryParams = Object.fromEntries(Object.entries(queryParams)
                .filter(([_, value]) => value !== null)
                .map(([key, value]) => [
                camelToSnakeCase(key),
                key === "claims" && typeof value === "object" ? JSON.stringify(value) : String(value),
            ]));
            const params = new URLSearchParams(filteredQueryParams);
            const completeUrl = `${this.environment.API_BASE_URL}/oauth/complete?${params.toString()}`;
            window.location.href = completeUrl;
        }
        catch (error) {
            console.error("Error completing OAuth flow:", error);
            const fallbackUrl = `${oauthParams.redirectUri}?error=redirect_error&error_description=OAuth redirect failed&state=${oauthParams.state}`;
            window.location.href = fallbackUrl;
        }
    }
    async persistNonOAuthFlowParams() {
        var _a, _b;
        const queryParams = effectiveQueryParams();
        const signedExternalId = queryParams.get("xid");
        const redirectUri = queryParams.get("redirectUri");
        const iss = queryParams.get("iss");
        const currentStudyId = (_b = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.studyId) !== null && _b !== void 0 ? _b : null;
        const lastStudyId = this.persistence.getHostedFlowStudyId();
        if (currentStudyId && lastStudyId && lastStudyId !== currentStudyId) {
            console.log(`[HostedFlow] New study ${currentStudyId} differs from last study ${lastStudyId}; clearing stale hosted-flow sessionStorage.`);
            this.persistence.clearHostedFlowSessionState();
        }
        if (signedExternalId) {
            this.persistence.setSignedExternalId(signedExternalId);
        }
        if (redirectUri && URL.canParse(redirectUri)) {
            this.persistence.setRedirectUriForHostedFlow(redirectUri);
        }
        if (currentStudyId && (signedExternalId !== null || redirectUri !== null || iss !== null)) {
            this.persistence.setHostedFlowStudyId(currentStudyId);
        }
        if (iss) {
            if (!currentStudyId)
                return;
            await when(() => this.featureManager.hasLoadedFeaturesForStudy(currentStudyId));
            await this.hostedIdpSsoManager.maybeStartSso(currentStudyId, iss);
        }
    }
    async handleRedirectForHostedFlow(status = "success") {
        var _a, _b;
        const redirectUrl = this.hostedRedirectUrl;
        if (!redirectUrl) {
            console.warn("No hosted redirect URL available");
            return;
        }
        try {
            const patientId = (_b = (_a = this.consentingPatientInfo) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : this.authManager.id;
            const studyId = this._session.studyId;
            const externalId = patientId && studyId ? await this.patientService.getPatientExternalId(patientId, studyId) : null;
            const url = new URL(redirectUrl);
            url.searchParams.set("status", status);
            if (patientId) {
                url.searchParams.set("patientId", patientId);
            }
            if (externalId) {
                url.searchParams.set("xid", externalId);
            }
            window.location.href = url.toString();
            this.persistence.setRedirectUriForHostedFlow(null);
            this.persistence.setIdpHintForAIFlow(null);
            this.persistence.setSignedExternalId(null);
            this.persistence.setIss(null);
        }
        catch (e) {
            console.error("[HostedFlow] Failed to parse redirect URL:", redirectUrl, e);
            this.done();
        }
    }
    async getDownloadPdfLink(retrievalToUse) {
        const retrieval = retrievalToUse !== null && retrievalToUse !== void 0 ? retrievalToUse : this.retrieval;
        if (!(retrieval === null || retrieval === void 0 ? void 0 : retrieval.batchId)) {
            console.warn(`No batch available`);
            return null;
        }
        return await this.documentRetrievalManager.getDownloadPdfLink(retrieval.batchId);
    }
    async getDownloadRawDocumentsLink(retrievalToUse) {
        const retrieval = retrievalToUse !== null && retrievalToUse !== void 0 ? retrievalToUse : this.retrieval;
        if (!(retrieval === null || retrieval === void 0 ? void 0 : retrieval.batchId)) {
            console.warn(`No batch available`);
            return null;
        }
        return await this.documentRetrievalManager.getDownloadDocumentsLink(retrieval.batchId);
    }
    updatePatientCollectedEmail(email) {
        var _a, _b, _c;
        this._session.patientCollectedEmail = email;
        this.persistence.setCookie(CookieName.PATIENT_COLLECTED_EMAIL, email, 365);
        const metadata = {
            email,
        };
        void this.analyticsService.trackPublicEvent({
            actorId: (_a = getAnonymousUserId()) !== null && _a !== void 0 ? _a : undefined,
            category: EventCategory.PATIENT,
            action: PatientAnalyticsEvent.EMAIL_COLLECTED,
            metadata,
        });
        const userId = (_b = getAnonymousUserId()) !== null && _b !== void 0 ? _b : undefined;
        const identity = (_c = this.authManager.id) !== null && _c !== void 0 ? _c : undefined;
        void this.analyticsService.trackHeapEvent({
            event: PatientAnalyticsEvent.EMAIL_COLLECTED,
            identity,
            user_id: userId,
            properties: { ...getHeapGlobalEventProperties(), isDataUpdate: this.isDataUpdate },
        });
    }
    updateUserChoseToUpdateRecords(userChoseToUpdateRecords) {
        this._session.userChoseToUpdateRecords = userChoseToUpdateRecords;
    }
    async reuseExistingRecords() {
        var _a, _b, _c;
        if (this.isInteractivePreview || this.isScreenshotPreviewMode) {
            return;
        }
        if (!((_a = this.existingRetrieval) === null || _a === void 0 ? void 0 : _a.batchId)) {
            console.warn(`No existing batch available`);
            return;
        }
        if (!this.reusingExistingRecords) {
            console.warn(`User didn't choose to reuse existing records`);
            return;
        }
        await this.documentRetrievalManager.reuseExistingDocuments(this.existingRetrieval, (_b = this.session.studyId) !== null && _b !== void 0 ? _b : null);
        void this.analyticsService.trackHeapEvent({
            event: PatientAnalyticsEvent.PATIENT_FLOW_COMPLETED,
            identity: (_c = this.authManager.id) !== null && _c !== void 0 ? _c : undefined,
            properties: {
                ...getHeapGlobalEventProperties(),
                completionReason: "Records Reused",
            },
        });
    }
    async getOptedInStudyConsentsByOrg() {
        this.studyManager.optedInPatientConsentsByStudyForSearch.reload();
        await this.studyManager.optedInPatientConsentsByStudyForSearch.waitForValue();
        if (this.studyManager.optedInPatientConsentsByStudyForSearch.value === NOT_LOADED)
            return null;
        const studySearchResult = this.studyManager.optedInPatientConsentsByStudyForSearch.value;
        return [
            ...studySearchResult
                .filter((result) => {
                if (!isProjectActiveForSharing(result))
                    return false;
                return result.consents.some((consent) => primaryConsentTypes.includes(consent.consentType) && isPatientActivelySharingConsent(consent));
            })
                .reduce((map, result) => {
                const org = result.addedByOrganization;
                if (!(org === null || org === void 0 ? void 0 : org.id) || map.has(org.id)) {
                    return map;
                }
                map.set(org.id, {
                    icon: result.organizationIconUrl,
                    orgId: org.id,
                    orgName: org.name,
                });
                return map;
            }, new Map())
                .values(),
        ];
    }
    setSelectedLoginType(selectedLoginType) {
        this._session.selectedLoginType = selectedLoginType;
    }
}
__decorate([
    observable
], PatientConsentUIManager.prototype, "_session", void 0);
__decorate([
    computed
], PatientConsentUIManager.prototype, "studyVersionId", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isDataUpdate", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isIAS", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "recordsSummary", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "existingRecordsSummary", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isPatientLoggedIn", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isResolvingResumeTarget", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "enteredViaResume", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "consentFlowPageData", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "studyType", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isInteractivePreview", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isScreenshotMode", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isScreenshotPreviewMode", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isPopup", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "previewMetadata", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isFirstRoute", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "currentPatientConsentRoute", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isInConsentFlow", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "patientName", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isParentGuardian", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "parentGuardianName", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "additionalParentGuardianName", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "patientMRN", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "providerName", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "patientFriendlyTitle", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "studySectionImages", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "studyInfo", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "studySponsor", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "studyPrimaryContactName", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "studyPrimaryContactTitle", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "primaryDiseaseOrConditions", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "patientFriendlySummary", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "irbApprovalStamp", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "primaryContactEmail", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "primaryContactPhone", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "primaryContactLink", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "risksOfEnrollment", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "benefitsOfEnrollment", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "aiToolName", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "alternativesToParticipation", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "resourceScopes", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "sensitivityScopes", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isFullRecordMode", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "exactConsentExpirationDate", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "consentExpirationDate", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isPDDAGeneralDataUse", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "showClassicLoginPage", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "rightToBeContacted", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "futureUseOfDataText", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "showBillOfRightsPage", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "allowsIdentifiedFutureDataUse", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "showSensitiveDataConfirmationPage", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isPDDAStudy", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "pddaHealthRecordsUpdateFrequency", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "pddaPatientRecordRetrievalRecencyFrequency", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "showTimeForVisitsPage", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "showEnrollmentDataSharingPage", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "showParticipationTerminationPage", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "showLeavingStudyEarlyPage", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "showCostOfParticipationPage", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "showSharingResultsPage", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "showCompensationPage", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "excludedReason", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "videoTranscode", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "require21CFRPart11ForInformedConsents", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "needsReauthorization", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isProvider", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "providerId", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isProxyAccess", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isParentGuardianAuthRequired", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isMinorConsentEnabled", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isMinor", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "showMinorConsentPages", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "showParticipantConsentPages", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isAdditionalParentGuardianEnabled", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isInterimPage", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "studyICFTranslationAvailableLanguages", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "currentLanguage", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "retrieval", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "initialLocationsNeedingAuthentication", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "pendingRetrieval", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "locatingPDDERecords", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "canAddLocations", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "zeroLocationsFound", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "hasPatientConsented", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "excludedPagesForReconsent", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "mayHaveAccessibleRecordsOnceReady", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isRelevantRoute", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isOnlyClearLoginAllowed", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isAppleLoginButtonEnabled", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "currentPage", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "pathLink", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "consentedByLink", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "currentPageIndex", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "patientCollectedEmail", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "existingRetrieval", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "existingRetrievalLastUpdatedAt", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "retrievalRecencyPolicyCutoffDate", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "showUseExistingRecordsPage", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "updatingRecords", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "gettingLatestRecords", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "reusingExistingRecords", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "showEmailCollectionPage", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "hasCompleteDemographicsFromClear", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "showAccountSetupPages", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "enrollmentPages", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "progressBarPercentage", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "consentingPatientInfo", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "homePageAfterConsent", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "currentRouteLocationIndex", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "useTefca", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "urlParams", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isAIFlow", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "isOAuthFlow", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "idpHint", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "signedExternalId", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "iss", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "allowedHostedRedirectUrls", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "hostedRedirectUrl", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "hasHostedRedirectUrl", null);
__decorate([
    computed
], PatientConsentUIManager.prototype, "canRedirectExternally", null);
function consentFlowProgressMilestoneForRoute(route) {
    if (route === "ENROLLMENT_SETUP_COMPLETION" || route === "ENROLLMENT_VERIFY_OTP")
        return { accountSetupShown: true };
    if (route === "ENROLLMENT_PARTICIPATION_SIGNATURE")
        return { consentShown: true };
    if (route === "ENROLLMENT_USE_EXISTING_RECORDS" || PDDA_ALL_ROUTES.includes(route))
        return { retrievalStarted: true };
    return undefined;
}
